import './style.css';
import {
  ACHIEVEMENTS,
  ADVENTURERS,
  CUSTOMERS,
  DAILY_COMMISSION_BONUS,
  DAILY_COMMISSIONS,
  DAILY_REWARDS,
  DUNGEONS,
  ITEMS,
  MARKET_PATH_REWARDS,
  MARKET_LISTINGS,
  MAX_INVENTORY_QUANTITY,
  RENOVATIONS,
  STORAGE_KEY,
  WEEKLY_CONTRACT,
  acceptCustomerOffer,
  addInventory,
  awardMarketXp,
  buyListing,
  claimDaily,
  claimDailyCommission,
  claimDailyCommissionBonus,
  claimAvailableMarketPathRewards,
  claimAvailableAchievements,
  claimAchievement,
  claimMarketPathReward,
  claimWeeklyContract,
  completeExpedition,
  createInitialState,
  drawRelics,
  declineCustomerOffer,
  dailyLoginStatus,
  ensureCustomerOffer,
  energyRecoveryForecast,
  expeditionDuration,
  expeditionForecast,
  hydrateState,
  haggleCustomerOffer,
  marketLevelProgress,
  markExpeditionAlerted,
  achievementProgress,
  recommendedDungeon,
  regenerateEnergy,
  renovationQuote,
  recordDiscoveries,
  recordDailyProgress,
  refreshAchievements,
  rollDailyState,
  rollWeeklyState,
  sellItem,
  setExpeditionAlert,
  startExpedition,
  summarizeAbsence,
  togglePartyMember,
  upgradeRenovation,
  weeklyStanding,
} from './core/GameState.js';
import {
  clearGameSave,
  parseGameSave,
  readGameSave,
  writeGameSave,
} from './core/GameStorage.js';
import {
  MAX_BACKUP_BYTES,
  parseGameBackup,
  serializeGameBackup,
} from './core/GameBackup.js';
import {
  nextUtcWeekBoundary,
  runtimeTickDelay,
  shouldPollSocial,
  SOCIAL_REFRESH_INTERVAL_MS,
} from './core/RuntimeSchedule.js';
import { dungeonMarketCloud } from './net/DungeonMarketCloud.js';
import { dungeonMarketAuth } from './net/DungeonMarketAuth.js';
import { MutationCoordinator } from './net/MutationCoordinator.js';
import { RefreshGeneration } from './net/RefreshGeneration.js';
import {
  normalizeCampaignClaimReceipt,
  normalizeCampaignStatus,
  normalizeCancellationReceipt,
  normalizeCloudLedgerReceipt,
  normalizeLeaderboardEntry,
  normalizeLanternReceipt,
  normalizeMarketListing,
  normalizePurchaseReceipt,
  normalizeRosterEntry,
  normalizeTradeHistoryEntry,
} from './net/CloudPayload.js';
import {
  listingEconomics,
  listingUnitPrice,
  medianUnitPrice,
  sortMarketListings,
  verifiedTradeAward,
  verifiedTradeXp,
} from './core/MarketMath.js';
import {
  applyLedgerDelta,
  applyLedgerSnapshot,
  chooseCloudLedger,
  cloudSyncRetryDelay,
  ledgerSnapshot,
  mergeCloudSyncReceipt,
  normalizeCloudSync,
  prepareCloudSync,
} from './net/CloudLedger.js';

const LEADERS = [
  { name: 'Sable & Sons', score: 18920, mark: '♛', tone: 'top' },
  { name: 'The Last Lantern', score: 16450, mark: '◈', tone: 'silver' },
  { name: 'Mossbeard', score: 13880, mark: '✦', tone: 'bronze' },
  { name: 'YOU', score: 0, mark: '↑ 2', tone: 'you' },
  { name: 'Gutter & Gold', score: 9240, mark: '—', tone: '' },
  { name: 'Velvet Hammer', score: 7710, mark: '↑ 1', tone: '' },
];

const CAMPAIGN_TIERS = Object.freeze([
  { tier: 1, target: 50, title: 'Trailhead Cache', reward: { gold: 500, gems: 3, marketScore: 150 } },
  { tier: 2, target: 200, title: 'Deep-Road Coffer', reward: { gold: 1200, gems: 6, marketScore: 400 } },
  { tier: 3, target: 500, title: 'Regent’s Treasury', reward: { gold: 2500, gems: 10, marketScore: 900 } },
]);
const KNOWN_ITEM_IDS = new Set(Object.keys(ITEMS));

const TAB_IDS = ['overview', 'dungeons', 'market', 'stall', 'collection', 'leaderboard', 'guild', 'friends'];

const icons = {
  dungeon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20V8l8-5 8 5v12M8 20v-6h8v6M9 9h.01M15 9h.01"/></svg>',
  chevron: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>',
};
const SAVE_WRITER_ID = syncEventId();

let returnReport = null;
let localSaveRevision = 0;
let externalSaveChange = null;
let browserStorage = null;
try {
  browserStorage = globalThis.localStorage;
} catch {
  // The storage adapter below will report the unavailable state.
}
let storageHealth = {
  issue: null,
  recoveryRaw: null,
  error: null,
};

function loadGame() {
  const now = Date.now();
  const result = readGameSave(browserStorage, STORAGE_KEY);
  if (result.status === 'unavailable') storageHealth.issue = 'read';
  if (result.status === 'corrupt') {
    storageHealth.issue = 'corrupt';
    storageHealth.recoveryRaw = result.recoveryRaw;
  }
  const saved = result.value;
  localSaveRevision = Number.isSafeInteger(Number(saved?.saveRevision))
    ? Math.max(0, Number(saved.saveRevision))
    : 0;
  const loaded = hydrateState(saved, now);
  returnReport = summarizeAbsence(saved, loaded, now);
  const requestedTab = new URLSearchParams(window.location.search).get('tab');
  if (TAB_IDS.includes(requestedTab)) loaded.activeTab = requestedTab;
  return loaded;
}

let state = loadGame();
const auth = dungeonMarketAuth();
const cloud = dungeonMarketCloud({
  sessionProvider: () => auth.session,
  sessionRefresher: async () => {
    await auth.restore();
    return {
      offline: auth.restoreOffline,
      retryable: auth.restoreRetryable,
      error: auth.restoreError,
    };
  },
});
let social = {
  mode: cloud.configured ? 'connecting' : 'guest',
  setupRequired: false,
  leaders: [],
  listings: [],
  mine: [],
  history: [],
  historyError: false,
  roster: [],
  rosterError: false,
  campaign: null,
  campaignError: false,
};
let toast = null;
const requestedModal = new URLSearchParams(window.location.search).get('modal');
let onboardingStep = 0;
let modal = requestedModal === 'reliquary'
  ? 'summon'
  : requestedModal === 'account'
    ? 'account'
    : requestedModal === 'market-path'
      ? 'market-path'
    : requestedModal === 'onboarding' || !state.onboardingComplete
      ? 'onboarding'
      : returnReport
        ? 'return-report'
        : null;
let listingItemId = null;
let listingDraftPrice = null;
let listingDraftQuantity = 1;
let marketSort = 'newest';
let marketCategory = 'all';
let summonResult = [];
let expeditionResult = null;
let accountMode = 'signin';
let accountPending = null;
let accountDraft = {
  identifier: '',
  signInPassword: '',
  username: '',
  email: '',
  signUpPassword: '',
};
let collectionFilter = 'All';
let timer = null;
let focusReturn = null;
let isOnline = navigator.onLine;
const pendingMutations = new Set();
let cloudReady = false;
let cloudConflict = null;
let pendingRestore = null;
let cloudSyncTimer = null;
let cloudSyncPromise = null;
let cloudSyncError = false;
let pwaUpdateReady = false;
let socialRefreshTimer = null;
const socialRefreshes = new RefreshGeneration();
const mutationCoordinator = new MutationCoordinator({
  getUserId: () => cloud.userId,
  flushLedger: flushCloudProgress,
  onLedgerReleased: () => scheduleCloudSync(
    cloudSyncError ? cloudSyncRetryDelay(cloudSyncError) : 450,
  ),
});

function persist({ cloud: shouldSyncCloud = true } = {}) {
  state = refreshAchievements(state);
  if (externalSaveChange) return false;
  if (storageHealth.issue !== 'read') {
    const nextRevision = localSaveRevision + 1;
    const result = writeGameSave(browserStorage, STORAGE_KEY, {
      ...state,
      activeTab: 'overview',
      lastActiveAt: Date.now(),
      saveRevision: nextRevision,
      saveWriterId: SAVE_WRITER_ID,
    });
    if (!result.ok) {
      storageHealth = {
        ...storageHealth,
        issue: 'write',
        error: result.error,
      };
    } else {
      localSaveRevision = nextRevision;
      if (storageHealth.issue === 'write') {
        storageHealth = { issue: null, recoveryRaw: null, error: null };
      }
    }
  }
  if (shouldSyncCloud) scheduleCloudSync();
  return true;
}

function syncEventId() {
  return globalThis.crypto?.randomUUID?.()
    || '00000000-0000-4000-8000-000000000000'.replace(/[018]/g, digit => (
      Number(digit) ^ Math.floor(Math.random() * 16) >> Number(digit) / 4
    ).toString(16));
}

function knownCloudLedger(value) {
  const snapshot = ledgerSnapshot(value);
  snapshot.inventory = Object.fromEntries(
    Object.entries(snapshot.inventory).filter(([itemId]) => ITEMS[itemId]),
  );
  return snapshot;
}

function scheduleCloudSync(delay = 450) {
  if (externalSaveChange || mutationCoordinator.ledgerActive || !cloudReady || !cloud.signedIn || !cloud.userId) return;
  clearTimeout(cloudSyncTimer);
  cloudSyncTimer = setTimeout(() => {
    syncCloudProgress();
  }, delay);
}

async function syncCloudProgress() {
  if (externalSaveChange || !cloudReady || !cloud.signedIn || !cloud.userId) return false;
  if (cloudSyncPromise) return cloudSyncPromise;
  const requestedUserId = cloud.userId;
  cloudSyncPromise = (async () => {
    const plan = prepareCloudSync(state, requestedUserId, syncEventId());
    if (!plan) return false;
    state = { ...state, cloudSync: plan.cloudSync };
    if (!plan.pending) {
      persist({ cloud: false });
      return true;
    }
    persist({ cloud: false });
    const result = await cloud.syncProgress(
      plan.pending.eventId,
      plan.pending.delta,
      plan.pending.target.discoveries,
    );
    if (cloud.userId !== requestedUserId) return false;
    if (!result.ok) {
      cloudSyncError = result.error || 'Cloud ledger synchronization failed.';
      scheduleCloudSync(cloudSyncRetryDelay(result.error));
      return false;
    }
    const receipt = normalizeCloudLedgerReceipt(
      Array.isArray(result.data) ? result.data[0] : result.data,
      KNOWN_ITEM_IDS,
    );
    if (!receipt) {
      cloudSyncError = true;
      scheduleCloudSync(15_000);
      return false;
    }
    state = mergeCloudSyncReceipt(state, requestedUserId, knownCloudLedger(receipt));
    state = recordDiscoveries(state, state.inventory.map(entry => entry.itemId));
    state = ensureCustomerOffer(state);
    cloudSyncError = false;
    persist({ cloud: false });
    render();
    scheduleCloudSync();
    return true;
  })().finally(() => {
    cloudSyncPromise = null;
  });
  return cloudSyncPromise;
}

async function flushCloudProgress() {
  clearTimeout(cloudSyncTimer);
  const synced = await syncCloudProgress();
  if (!synced) showToast('Your cloud ledger could not be synchronized. Try again when the bazaar reconnects.');
  return synced;
}

function rebaseCloudLedger(delta = {}, overrides = {}) {
  const sync = normalizeCloudSync(state.cloudSync, cloud.userId);
  if (!sync) return;
  const baseline = {
    ...applyLedgerDelta(sync.baseline, delta),
    ...overrides,
  };
  state = {
    ...state,
    cloudSync: {
      userId: cloud.userId,
      baseline: ledgerSnapshot(baseline),
      pending: null,
    },
  };
}

function reconcileCloudBootstrap(result, allowConflict = true) {
  if (!result?.ok || !cloud.userId) return false;
  const receipt = normalizeCloudLedgerReceipt(
    Array.isArray(result.data) ? result.data[0] : result.data,
    KNOWN_ITEM_IDS,
    { bootstrap: true },
  );
  if (!receipt) return false;
  const server = { ...knownCloudLedger(receipt), is_new: receipt.is_new };
  const existing = normalizeCloudSync(state.cloudSync, cloud.userId);
  if (!existing) {
    if (!server.is_new && allowConflict) {
      cloudConflict = { userId: cloud.userId, server };
      if (!modal || modal === 'account') modal = 'cloud-conflict';
      return false;
    }
    state = chooseCloudLedger(state, cloud.userId, server, 'device');
    persist({ cloud: false });
    return true;
  }
  state = { ...state, cloudSync: existing };
  const plan = prepareCloudSync(state, cloud.userId, syncEventId());
  if (plan?.pending) {
    state = { ...state, cloudSync: plan.cloudSync };
    persist({ cloud: false });
    return true;
  }
  state = {
    ...applyLedgerSnapshot(state, server),
    cloudSync: {
      userId: cloud.userId,
      baseline: ledgerSnapshot(server),
      pending: null,
    },
  };
  state = recordDiscoveries(state, state.inventory.map(entry => entry.itemId));
  state = ensureCustomerOffer(state);
  persist({ cloud: false });
  return true;
}

function commit(result, closeModal = false) {
  const previousLevel = state.level;
  if (result.state) state = ensureCustomerOffer(result.state);
  const closed = Boolean(closeModal && result.ok);
  if (closed) modal = null;
  persist();
  showToast(progressionMessage(result.message, previousLevel));
  if (closed) restoreFocus();
}

function beginMutation(key, element, pendingLabel) {
  if (pendingMutations.has(key)) return false;
  pendingMutations.add(key);
  if (element) {
    element.disabled = true;
    element.setAttribute('aria-busy', 'true');
    if (pendingLabel) element.textContent = pendingLabel;
  }
  return true;
}

function endMutation(key) {
  pendingMutations.delete(key);
}

async function runMutation(key, element, pendingLabel, operation, { ledger = false } = {}) {
  if (ledger && mutationCoordinator.ledgerActive) return null;
  if (!beginMutation(key, element, pendingLabel)) return null;
  try {
    const outcome = await mutationCoordinator.run(key, operation, { ledger });
    return outcome.started ? outcome.result : null;
  } catch (error) {
    return { ok: false, error: error?.message || 'The shared bazaar could not complete that request.' };
  } finally {
    endMutation(key);
  }
}

function money(value) {
  return Math.max(0, Math.round(value)).toLocaleString('en-US');
}

function escapeText(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function escapeInitial(value) {
  return escapeText(Array.from(String(value || 'M').trim())[0] || 'M').toUpperCase();
}

function duration(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function awayDuration(ms) {
  const totalMinutes = Math.max(1, Math.floor(ms / 60_000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function dateLine() {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date()).toUpperCase();
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function itemIcon(item) {
  return `<span class="item-icon ${item.tone}">${item.icon}</span>`;
}

function selectedDungeon() {
  return state.expedition
    ? DUNGEONS.find(dungeon => dungeon.id === state.expedition.dungeonId)
    : DUNGEONS[1];
}

function render() {
  clearTimeout(timer);
  const levelProgress = marketLevelProgress(state);
  document.querySelector('#app').innerHTML = `
    <div class="app-shell">
      <aside class="sidebar">
        <div class="brand">
          <div class="brand-mark">◆</div>
          <div><div class="brand-name">DUNGEON<br><span>MARKET</span></div><div class="brand-sub">The depths are open</div></div>
        </div>
        <button class="season-card" data-action="open-market-path" aria-label="Open The Gilded Descent Market Path">
          <div class="season-top"><span class="live-dot"></span> SEASON 04 <span class="season-timer">MARKET PATH</span></div>
          <div class="season-name">The Gilded Descent ${marketPathReadyCount() ? `<b>${marketPathReadyCount()} READY</b>` : ''}</div>
          <div class="season-progress" role="progressbar" aria-label="Market level progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${levelProgress.percent}"><span style="width:${levelProgress.percent}%"></span></div>
          <div class="season-foot"><span>Market level ${state.level}</span><span>${levelProgress.remaining ? `${levelProgress.remaining} XP to Lv.${state.level + 1}` : 'Maximum level'}</span></div>
        </button>
        <nav class="main-nav" aria-label="Main navigation">
          ${navItem('overview', 'Overview', '⌂')}
          ${navItem('dungeons', 'Dungeons', '♧', state.expedition ? '1' : '3')}
          ${navItem('market', 'Market', '◈')}
          ${navItem('stall', 'My Stall', '⌂', state.stall.offer ? '1' : '')}
          ${navItem('collection', 'Collection', '▣', inventoryCount())}
          ${navItem('leaderboard', 'Leaderboard', '♛')}
          ${navItem('guild', 'Guild', '♜', guildReadyCount() || '', 'mobile-nav-only')}
          ${navItem('friends', 'Friends', '♧', availableLanterns() || '', 'mobile-nav-only')}
        </nav>
        <div class="sidebar-rule"></div>
        <div class="nav-label">COMMUNITY</div>
        <div class="community-nav">
          ${navItem('guild', 'Copper & Clover', '♜', guildReadyCount() || '')}
          ${navItem('friends', 'Friends', '♧', availableLanterns() || '')}
        </div>
        <div class="sidebar-spacer"></div>
        <div class="player-card">
          <div class="avatar">${playerName().charAt(0).toUpperCase()}</div>
          <div class="player-info"><strong>${playerName()}</strong><span>${auth.signedIn ? 'Cloud merchant' : 'Guest'} · Level ${state.level}</span></div>
          <button class="icon-btn account-button" data-action="open-account" aria-label="Open account" title="Account">${auth.signedIn ? '●' : '⋮'}</button>
        </div>
      </aside>
      <main class="main-content" id="main-content" tabindex="-1">
        <header class="topbar">
          <div class="breadcrumb"><span>THE BAZAAR</span><i>›</i><strong>${titleCase(state.activeTab)}</strong></div>
          <div class="top-actions">
            <button class="path-chip" data-action="open-market-path" aria-label="Open Market Path, level ${state.level}${marketPathReadyCount() ? `, ${marketPathReadyCount()} rewards ready` : ''}"><span>♛</span><strong>LV.${state.level}</strong>${marketPathReadyCount() ? `<b>${marketPathReadyCount()}</b>` : ''}</button>
            <div class="resource gold" aria-label="${money(state.gold)} gold"><span class="coin" aria-hidden="true">◆</span><strong>${money(state.gold)}</strong><button class="add-btn" data-tab="market" aria-label="Open market">+</button></div>
            <div class="resource gems" aria-label="${state.gems} ${state.gems === 1 ? 'gem' : 'gems'}"><span aria-hidden="true">✦</span><strong>${state.gems}</strong><button class="add-btn" data-action="open-summon" aria-label="Open Reliquary">+</button></div>
            <button class="notification" data-tab="guild" aria-label="Open guild journal"><span aria-hidden="true">♢</span><span aria-hidden="true"></span></button>
            <button class="mobile-account-button" data-action="open-account" aria-label="Open merchant account">${escapeInitial(auth.username || 'Marrowmancer')}</button>
          </div>
        </header>
        <section class="content">${crossTabNotice()}${storageNotice()}${pwaUpdateNotice()}${view()}</section>
      </main>
    </div>
    ${modal ? modalView() : ''}
    <div class="toast ${toast ? 'show' : ''}" role="status" aria-live="polite" aria-atomic="true">${escapeText(toast || '')}</div>
  `;
  bindEvents();
  prepareModal();
  revealActiveNavigation();
  scheduleRuntimeTick();
}

function crossTabNotice() {
  if (!externalSaveChange) return '';
  return `<section class="storage-notice warning" role="alert"><span>⇄</span><div><strong>Another tab updated this ledger.</strong><p>Gameplay and cloud sync are paused here to prevent one tab from overwriting the other.</p></div><div class="storage-notice-actions"><button class="button gold-button" data-action="adopt-external-save">Load newer tab</button><button class="button secondary" data-action="keep-local-save">Keep this tab</button></div></section>`;
}

function storageNotice() {
  if (storageHealth.issue === 'corrupt') {
    return `<section class="storage-notice recovery" role="status"><span>◇</span><div><strong>Damaged save preserved.</strong><p>A fresh local ledger is running. The original browser data was quarantined for recovery instead of being discarded.</p></div><div class="storage-notice-actions"><button class="button secondary" data-action="download-save-recovery">Download copy</button><button class="text-button" data-action="dismiss-save-recovery">Dismiss</button></div></section>`;
  }
  if (storageHealth.issue === 'read') {
    return `<section class="storage-notice warning" role="status"><span>!</span><div><strong>Browser save access is blocked.</strong><p>This session is temporary. Retrying will restore an existing ledger if storage becomes available.</p></div><button class="button secondary" data-action="retry-storage">Retry storage</button></section>`;
  }
  if (storageHealth.issue === 'write') {
    return `<section class="storage-notice warning" role="status"><span>!</span><div><strong>Progress is not being saved locally.</strong><p>Storage may be full or restricted. Gameplay can continue while you free space or change browser permissions.</p></div><button class="button secondary" data-action="retry-storage">Retry save</button></section>`;
  }
  return '';
}

function pwaUpdateNotice() {
  if (!pwaUpdateReady) return '';
  return `<section class="storage-notice update" role="status"><span>↻</span><div><strong>A new market ledger is ready.</strong><p>The update is active. Reload when convenient to use the latest interface; your current progress has already been preserved.</p></div><button class="button gold-button" data-action="reload-update">Save & reload</button></section>`;
}

function navItem(id, label, icon, badge, className = '') {
  const badgeLabel = navBadgeLabel(id, badge);
  return `<button class="nav-item ${className} ${state.activeTab === id ? 'active' : ''}" data-tab="${id}" ${state.activeTab === id ? 'aria-current="page"' : ''} aria-label="${label}${badgeLabel ? `, ${badgeLabel}` : ''}">
    <span class="nav-icon" aria-hidden="true">${icon}</span><span>${label}</span>${badge ? `<b aria-hidden="true">${badge}</b>` : ''}
  </button>`;
}

function navBadgeLabel(id, badge) {
  if (!badge) return '';
  if (id === 'dungeons') return state.expedition ? 'one active expedition' : `${badge} routes available`;
  if (id === 'stall') return `${badge} customer waiting`;
  if (id === 'collection') return `${badge} items owned`;
  if (id === 'guild') return `${badge} rewards ready`;
  if (id === 'friends') return `${badge} lanterns available`;
  return `${badge} updates`;
}

function revealActiveNavigation() {
  if (!window.matchMedia('(max-width: 680px)').matches) return;
  const active = document.querySelector('.main-nav .nav-item.active');
  requestAnimationFrame(() => active?.scrollIntoView({
    behavior: 'auto',
    block: 'nearest',
    inline: 'center',
  }));
}

function view() {
  const views = {
    overview,
    dungeons: dungeonView,
    market: marketView,
    stall: stallView,
    collection: collectionView,
    leaderboard: leaderboardView,
    guild: guildView,
    friends: friendsView,
  };
  return (views[state.activeTab] || overview)();
}

function clearAccountDraft() {
  accountDraft = {
    identifier: '',
    signInPassword: '',
    username: '',
    email: '',
    signUpPassword: '',
  };
}

async function runAccountOperation(kind, operation) {
  if (accountPending) return null;
  accountPending = kind;
  render();
  try {
    return await operation();
  } catch (error) {
    return { ok: false, error: error?.message || 'The account service could not complete that request.' };
  } finally {
    accountPending = null;
  }
}

function pageHeading(kicker, title, description, action = '') {
  return `<div class="page-heading">
    <div><p class="eyebrow">${kicker}</p><h1>${title}</h1><p class="lede">${description}</p></div>${action}
  </div>`;
}

function connectionStatus() {
  if (!isOnline) return { className: 'offline', label: '○ OFFLINE' };
  if (!cloud.configured || social.setupRequired) return { className: 'local', label: '◇ LOCAL MODE' };
  if (cloud.signedIn) return { className: 'shared', label: '● SHARED ONLINE' };
  return { className: '', label: '● ONLINE' };
}

function overview() {
  const login = dailyLoginStatus(state);
  const loginReward = login.reward;
  const connection = connectionStatus();
  const liveEntry = social.mode === 'live'
    ? social.leaders.find(leader => leader.is_you)
    : null;
  const thisWeek = social.mode === 'live'
    ? Math.max(0, Number(liveEntry?.weekly_score) || 0)
    : weeklyStanding(state);
  const weeklyCaption = thisWeek > 0
    ? `${money(thisWeek)} ${social.mode === 'live' ? 'verified' : 'local'} this week`
    : `No ${social.mode === 'live' ? 'verified' : 'local'} standing this week`;
  return `
    <div class="welcome-row">
      <div><p class="eyebrow">${dateLine()} <span class="status-pill ${connection.className}" data-connection-status>${connection.label}</span></p><h1>${greeting()}, <em>${playerName()}.</em></h1><p class="lede">The lanterns are lit. Your adventurers are waiting at the gate.</p></div>
      <button class="button primary" data-action="open-dungeon">${icons.dungeon} Explore a dungeon <span>→</span></button>
    </div>
    <div class="stats-grid">
      ${statCard('Lifetime standing', money(state.marketScore), standingLabel(), weeklyCaption, '♛', 'orange')}
      ${statCard('Today’s takings', money(state.todayHaul), 'expedition &amp; customer gold', state.todayHaul ? 'Earned since the market bell' : 'No returns or customer sales yet', '◆', 'gold')}
      ${statCard('Energy', `${state.energy} / ${state.maxEnergy}`, energyCaption(), `${Math.round(state.energy / state.maxEnergy * 100)}% available`, 'ϟ', 'teal', true)}
      ${statCard('Collection', inventoryCount(), `${discoveredCount()} discoveries`, `${relicCount()} relic${relicCount() === 1 ? '' : 's'} displayed`, '▣', 'violet')}
    </div>
    <div class="dashboard-grid">
      <section class="panel expedition-panel">
        <div class="panel-heading"><div><p class="eyebrow">${state.expedition ? 'EXPEDITION ACTIVE' : 'YOUR NEXT RUN'}</p><h2>Expedition board</h2></div><button class="text-button" data-tab="dungeons">View all ${icons.chevron}</button></div>
        ${state.expedition ? expeditionActive() : expeditionFeature()}
      </section>
      <section class="panel login-panel">
        <div class="panel-heading"><div><p class="eyebrow">KEEPER’S STREAK</p><h2>Keeper’s calendar</h2></div><span class="streak-badge">✦ ${state.streak} consecutive day${state.streak === 1 ? '' : 's'}</span></div>
        <div class="calendar-row">${DAILY_REWARDS.map(reward => calendarDay(reward, login)).join('')}</div>
        <div class="calendar-meta"><span>${login.clockSkew ? 'Calendar paused until the recorded date catches up' : login.claimed ? `Cycle Day ${login.cycleDay} secured` : `Next: ${money(loginReward.gold)} ◆${loginReward.gems ? ` + ${loginReward.gems} ✦` : ''}`}</span><strong>${login.streakWillReset ? 'Missed bell · streak restarts on claim' : 'Day 7 loops back to Day 1 · streak continues'}</strong></div>
        <button class="button ${login.canClaim ? 'gold-button' : 'secondary claimed'} full" data-action="claim-login" ${login.canClaim ? '' : 'disabled'}>
          ${login.canClaim ? `Claim Cycle Day ${login.rewardDay} <span>→</span>` : login.clockSkew ? 'Calendar waiting for date' : '✓ Reward claimed today'}
        </button>
      </section>
    </div>
    <div class="lower-grid">
      <section class="panel market-panel">
        <div class="panel-heading"><div><p class="eyebrow">DAILY KEEPER STOCK</p><h2>Trending wares</h2></div><button class="text-button" data-tab="market">Open market ${icons.chevron}</button></div>
        <div class="market-list">${availableListings().slice(0, 3).map(marketRow).join('') || soldOutMessage()}</div>
        <div class="market-footer"><span><i class="live-dot"></i> Keeper listings refresh daily</span><span>Shared seller fee: 0.5% · minimum 1 ◆</span></div>
      </section>
      <section class="panel leaderboard-panel">
        <div class="panel-heading"><div><p class="eyebrow">${social.mode === 'live' ? 'VERIFIED THIS WEEK' : 'LOCAL PRACTICE BOARD'}</p><h2>${social.mode === 'live' ? 'Leaderboard' : 'Fictional rivals'}</h2></div><button class="text-button" data-tab="leaderboard">${social.mode === 'live' ? 'Full rankings' : 'Open practice board'} ${icons.chevron}</button></div>
        <div class="leaderboard-list">${rankedLeaders().slice(0, 4).map(leaderRow).join('')}</div>
      </section>
    </div>
    <section class="feature-strip">
      <div class="feature-copy"><span class="feature-kicker">THE RELIQUARY</span><h2>Every relic has a story.</h2><p>Draw artifacts from the sealed collection. A legendary discovery is guaranteed within ${10 - state.pity} draw${10 - state.pity === 1 ? '' : 's'}.</p><button class="button dark-button" data-action="open-summon">Visit the Reliquary <span>→</span></button></div>
      <div class="relic-art"><div class="glow-orb"></div><div class="relic">♕</div><div class="spark s1">✦</div><div class="spark s2">✧</div><div class="spark s3">·</div></div>
    </section>`;
}

function statCard(title, value, sub, trend, icon, tone, energy = false) {
  return `<article class="stat-card"><div class="stat-top"><span>${title}</span><span class="stat-icon ${tone}">${icon}</span></div><div class="stat-value"${energy ? ' data-energy-value' : ''}>${value}</div><div class="stat-sub"${energy ? ' data-energy-caption' : ''}>${sub}</div>${energy ? `<div class="energy-bar"><span data-energy-bar style="width:${Math.round(state.energy / state.maxEnergy * 100)}%"></span></div><div class="energy-forecast" data-energy-forecast>${energyFullCaption()}</div>` : `<div class="stat-trend ${tone === 'violet' ? 'purple' : ''}">${trend}</div>`}</article>`;
}

function expeditionFeature() {
  const dungeon = recommendedDungeon(state);
  const forecast = expeditionForecast(state, dungeon.id);
  return `<div class="expedition-hero">
    <div class="dungeon-illustration"><div class="moon"></div><div class="mountain m1"></div><div class="mountain m2"></div><div class="ruin"><span>♜</span></div><div class="fireflies">·　✦　·</div></div>
    <div class="expedition-details"><div class="tag cyan-tag">RECOMMENDED FOR YOU</div><h3>${dungeon.name}</h3><p>“${dungeon.quote}”</p>
      <div class="detail-line"><span>♧ Base gold · before crew perks</span><strong>${money(dungeon.rewardMin)}–${money(dungeon.rewardMax)} <small>◆</small></strong></div>
      <div class="detail-line"><span>♢ Expedition time</span><strong>${duration(expeditionDuration(state, dungeon))}</strong></div>
      <div class="detail-line"><span>♜ Success forecast</span><strong class="forecast-${forecast.rating.toLowerCase()}">${Math.round(forecast.successChance * 100)}% · ${forecast.rating}</strong></div>
      <div class="detail-line"><span>ϟ Energy cost</span><strong>${dungeon.cost}</strong></div>
      <button class="button ${state.energy >= dungeon.cost && !state.expedition ? 'primary' : 'secondary'} full" data-action="start-expedition" data-dungeon="${dungeon.id}" ${state.energy >= dungeon.cost && !state.expedition ? '' : 'disabled'}>Send your party <span>→</span></button>
    </div>
  </div>`;
}

function expeditionActive() {
  const dungeon = selectedDungeon();
  const total = state.expedition.endsAt - state.expedition.startedAt;
  const remaining = Math.max(0, state.expedition.endsAt - Date.now());
  const progress = Math.min(100, Math.max(0, 100 - (remaining / total) * 100));
  const ready = remaining <= 0;
  const partyIds = state.expedition.partyIds || state.selectedParty;
  const alertArmed = Boolean(state.expedition.notifyOnComplete);
  const alertPermission = expeditionAlertPermission();
  const alertLabel = alertPermission === 'denied'
    ? 'Alerts blocked'
    : alertPermission === 'unsupported'
      ? 'Alerts unavailable'
      : alertArmed
        ? '✓ Alert armed'
        : '♢ Alert me when ready';
  return `<div class="active-expedition">
    <div class="active-icon ${dungeon.tone}">${dungeon.icon}</div>
    <div><span class="tag ${ready ? 'gold-tag' : 'green-tag'}">${ready ? 'HAUL READY' : 'PARTY IN THE DEPTHS'}</span><h3>${dungeon.name}</h3><p>${ready ? 'The gate bell is ringing. Your party has returned.' : 'Your party is searching for something worth selling.'}</p>
      <div class="run-party">${partyIds.map(id => `<span class="${ADVENTURERS[id].tone}" title="${ADVENTURERS[id].name}">${ADVENTURERS[id].icon}</span>`).join('')}<small>${state.expedition.party?.synergy || 'Expedition crew'}</small></div>
      <div class="run-bar"><span data-run-bar style="width:${progress}%"></span></div><div class="run-meta"><span data-run-time>${ready ? 'Ready to collect' : `${duration(remaining)} remaining`}</span><strong data-run-progress>${Math.round(progress)}%</strong></div>
    </div>
    <div class="run-actions">
      ${ready ? '' : `<button class="run-alert ${alertArmed ? 'armed' : ''}" data-action="toggle-expedition-alert" aria-pressed="${alertArmed}" title="${alertPermission === 'denied' ? 'Allow notifications in your browser settings to use expedition alerts.' : alertPermission === 'unsupported' ? 'This browser does not support expedition alerts.' : 'Keep Dungeon Market open in this browser and this tab will alert you when the crew returns.'}" ${alertPermission === 'denied' || alertPermission === 'unsupported' ? 'disabled' : ''}>${alertLabel}</button>`}
      <button class="button ${ready ? 'gold-button' : 'secondary'}" data-action="complete-expedition" ${ready ? '' : 'disabled'}>${ready ? 'Collect haul' : 'In progress'}</button>
    </div>
  </div>`;
}

function calendarDay(reward, status) {
  const done = status.claimed
    ? reward.day <= status.cycleDay
    : status.rewardDay > 1 && reward.day < status.rewardDay;
  const today = status.canClaim && reward.day === status.rewardDay;
  const gold = reward.gold >= 1000 ? `${(reward.gold / 1000).toFixed(reward.gold % 1000 ? 1 : 0)}k` : reward.gold;
  return `<div class="calendar-day ${done ? 'done' : ''} ${today ? 'today' : ''}" title="Cycle Day ${reward.day}: ${money(reward.gold)} gold${reward.gems ? ` and ${reward.gems} ${reward.gems === 1 ? 'gem' : 'gems'}` : ''}"><span class="day-num">${reward.day}</span><div class="day-reward">${done ? '✓' : `<b>${gold} ◆</b>${reward.gems ? `<small>+${reward.gems} ✦</small>` : ''}`}</div></div>`;
}

function dungeonView() {
  return `${pageHeading('EXPEDITION GATE', 'Choose your depth.', 'Each route trades energy and time for gold, experience, and loot from its own table.')}
    ${partyBuilder()}
    ${state.expedition ? `<section class="panel current-run"><div class="panel-heading"><div><p class="eyebrow">CURRENT RUN</p><h2>One party beyond the gate</h2></div></div>${expeditionActive()}</section>` : ''}
    <div class="dungeon-grid">${DUNGEONS.map(dungeonCard).join('')}</div>`;
}

function partyBuilder(compact = false) {
  const forecast = expeditionForecast(state, 'catacombs');
  const locked = Boolean(state.expedition);
  return `<section class="panel party-builder ${compact ? 'compact' : ''}">
    <div class="party-heading"><div><p class="eyebrow">EXPEDITION PARTY</p><h2>${forecast.party.synergy}</h2><p>${locked ? 'This crew is committed until the current expedition returns.' : 'Choose two or three adventurers. Distinct roles earn a balanced-crew bonus.'}</p></div>
      <div class="party-stats"><span>⚔ <strong>${forecast.party.power}</strong> power</span><span>✦ <strong>${forecast.party.luck}</strong> luck</span></div>
    </div>
    <div class="roster-row">${state.adventurers.map(entry => adventurerCard(entry, locked)).join('')}</div>
  </section>`;
}

function adventurerCard(entry, locked) {
  const adventurer = ADVENTURERS[entry.id];
  const selected = state.selectedParty.includes(entry.id);
  const nextLevelXp = entry.level * 100;
  const progress = entry.level >= 10 ? 100 : Math.min(100, (entry.xp - (entry.level - 1) * 100) / 100 * 100);
  return `<button class="adventurer-card ${adventurer.tone} ${selected ? 'selected' : ''}" data-action="toggle-party" data-adventurer="${entry.id}" aria-pressed="${selected}" ${locked ? 'disabled' : ''}>
    <span class="adventurer-check">${selected ? '✓' : '+'}</span><span class="adventurer-portrait">${adventurer.icon}</span>
    <span class="adventurer-copy"><b>${adventurer.name}</b><small>Lv. ${entry.level} · ${adventurer.role}</small><em>${adventurer.trait}</em></span>
    <span class="adventurer-numbers"><i>⚔ ${adventurer.power + Math.floor((entry.level - 1) / 2)}</i><i>✦ ${adventurer.luck}</i></span>
    <span class="adventurer-xp"><i style="width:${progress}%"></i></span><span class="sr-only">${entry.level >= 10 ? 'Maximum level' : `${entry.xp} of ${nextLevelXp} experience`}</span>
  </button>`;
}

function dungeonCard(dungeon) {
  const locked = Boolean(state.expedition);
  const forecast = expeditionForecast(state, dungeon.id);
  return `<article class="depth-card ${dungeon.tone}">
    <div class="depth-scene"><span class="depth-glyph">${dungeon.icon}</span><span class="risk ${dungeon.tone}">${dungeon.risk} risk</span></div>
    <div class="depth-body"><p class="eyebrow">RECOMMENDED LEVEL ${dungeon.level}</p><h2>${dungeon.name}</h2><p class="depth-quote">“${dungeon.quote}”</p>
      <div class="depth-rewards"><span>Base gold<strong>${money(dungeon.rewardMin)}–${money(dungeon.rewardMax)} ◆</strong></span><span>Forecast<strong class="forecast-${forecast.rating.toLowerCase()}">${Math.round(forecast.successChance * 100)}% · ${forecast.rating}</strong></span></div>
      <div class="loot-preview">${dungeon.loot.slice(0, 3).map(id => itemIcon(ITEMS[id])).join('')}<span>+ rare finds · ${duration(expeditionDuration(state, dungeon))}</span></div>
      <button class="button ${locked || state.energy < dungeon.cost ? 'secondary' : 'primary'} full" data-action="start-expedition" data-dungeon="${dungeon.id}" ${locked || state.energy < dungeon.cost ? 'disabled' : ''}>${locked ? 'Party already deployed' : state.energy < dungeon.cost ? `Need ${dungeon.cost} energy` : `Explore · ${dungeon.cost} ϟ`}</button>
    </div>
  </article>`;
}

function marketView() {
  return `${pageHeading('THE NIGHT BAZAAR', 'The bazaar never sleeps.', 'Buy daily keeper listings, trade with players when the shared bazaar is available, or sell discoveries at the keeper’s counter.', `<div class="heading-actions">${socialBadge()}<button class="button dark-button" data-tab="collection">Sell from collection <span>→</span></button></div>`)}
    <div class="market-summary">
      <div><span>Available listings</span><strong>${availableListings().length}</strong></div>
      <div><span>Your purse</span><strong>${money(state.gold)} ◆</strong></div>
      <div><span>Trade reputation</span><strong>${money(state.marketScore)}</strong></div>
    </div>
    ${onlineMarket()}
    <section class="panel market-browser"><div class="panel-heading"><div><p class="eyebrow">DAILY STOCK</p><h2>Keeper listings</h2></div><span class="market-clock"><i class="live-dot"></i> refreshes in <b data-market-clock>${marketBellCaption()}</b></span></div>
      <div class="listing-grid">${availableListings().map(listingCard).join('') || soldOutMessage()}</div>
    </section>`;
}

function socialBadge() {
  if (!isOnline) return '<span class="cloud-badge offline" data-social-badge><i></i>offline ledger</span>';
  if (social.setupRequired) return '<span class="cloud-badge offline" data-social-badge><i></i>setup required</span>';
  if (cloud.signedIn && cloudSyncError) {
    const error = String(cloudSyncError);
    const deferred = /reconciliation.*(?:budget|limit reached)/i.test(error);
    return `<span class="cloud-badge offline" data-social-badge title="${escapeText(error)}"><i></i>${deferred ? 'sync queued' : 'ledger retrying'}</span>`;
  }
  if (cloud.signedIn && (cloudSyncPromise || state.cloudSync?.pending)) return '<span class="cloud-badge cloud" data-social-badge><i></i>syncing ledger</span>';
  const labels = {
    connecting: ['connecting', 'cloud'],
    live: ['shared bazaar synced', 'live'],
    guest: ['guest bazaar', 'guest'],
  };
  const [label, tone] = labels[social.mode];
  return `<span class="cloud-badge ${tone}" data-social-badge><i></i>${label}</span>`;
}

function onlineMarket() {
  if (social.mode === 'connecting') {
    return `<section class="panel online-market loading-market"><span class="cloud-spinner"></span><div><strong>Opening the shared bazaar…</strong><p>Keeper listings remain available while the lantern is lit.</p></div></section>`;
  }
  if (social.mode !== 'live') {
    return `<section class="panel guest-market-note"><div><span>◇</span><div><strong>${social.setupRequired ? 'Shared bazaar setup required' : 'Guest bazaar'}</strong><p>Apply the included Supabase schema to enable cross-player listings and global standings.</p></div></div></section>`;
  }
  const mineIds = new Set(social.mine.map(listing => listing.id));
  const categories = [...new Set(social.listings
    .map(listing => ITEMS[listing.item_id]?.category)
    .filter(Boolean))].sort();
  const sharedListings = sortMarketListings(social.listings
    .filter(listing => !mineIds.has(listing.id))
    .filter(listing => marketCategory === 'all' || ITEMS[listing.item_id]?.category === marketCategory), marketSort);
  return `${myListingsPanel()}<section class="panel online-market">
    <div class="panel-heading market-board-heading"><div><p class="eyebrow">CROSS-PLAYER TRADING</p><h2>Shared bazaar</h2></div><div class="market-board-tools"><label>Category<select data-market-category><option value="all">All wares</option>${categories.map(category => `<option value="${category}" ${marketCategory === category ? 'selected' : ''}>${category}</option>`).join('')}</select></label><label>Sort<select data-market-sort><option value="newest" ${marketSort === 'newest' ? 'selected' : ''}>Newest</option><option value="unit-low" ${marketSort === 'unit-low' ? 'selected' : ''}>Unit price ↑</option><option value="unit-high" ${marketSort === 'unit-high' ? 'selected' : ''}>Unit price ↓</option><option value="quantity" ${marketSort === 'quantity' ? 'selected' : ''}>Largest bundle</option></select></label><span class="market-clock"><i class="live-dot"></i>${cloud.signedIn ? ` ${escapeText(cloud.username)}` : ' public'}</span></div></div>
    <div class="online-list">${sharedListings.map(onlineListingRow).join('') || soldOutMessage(true)}</div>
  </section>`;
}

function liveUnitMedian(itemId) {
  return medianUnitPrice([...social.listings, ...social.mine], itemId);
}

function myListingsPanel() {
  if (!cloud.signedIn) return '';
  return `<section class="panel online-market my-listings">
    <div class="panel-heading"><div><p class="eyebrow">YOUR ESCROW</p><h2>Active listings</h2></div><span class="listing-count">${social.mine.length} / 30 slots</span></div>
    <div class="online-list">${social.mine.length ? social.mine.map(listing => {
      const item = ITEMS[listing.item_id];
      const pending = pendingMutations.has(`cancel-listing:${listing.id}`);
      const owned = state.inventory.find(entry => entry.itemId === listing.item_id)?.quantity || 0;
      const returnFits = owned + listing.quantity <= MAX_INVENTORY_QUANTITY;
      const disabled = pending || !returnFits;
      const buttonLabel = pending ? 'Returning…' : returnFits ? 'Cancel & return' : 'Make room first';
      return `<div class="online-listing own-listing"><div>${itemIcon(item)}<div><strong>${item.name}</strong><span>${listing.quantity}× in escrow · ${money(listingUnitPrice(listing))} each</span></div></div><strong>${money(Number(listing.price))} ◆</strong><button class="buy-btn cancel-listing-btn" data-action="cancel-online-listing" data-listing="${listing.id}" ${disabled ? `disabled${pending ? ' aria-busy="true"' : ` title="Sell or list ${money(owned + listing.quantity - MAX_INVENTORY_QUANTITY)} first to make room"`}` : ''}>${buttonLabel}</button></div>`;
    }).join('') : `<div class="empty-listings"><span>◇</span><div><strong>No stock in escrow.</strong><p>List an item from your collection to offer it to other merchants.</p></div></div>`}</div>
  </section>${tradeHistoryPanel()}`;
}

function tradeHistoryPanel() {
  if (social.historyError) {
    return `<section class="panel trade-history-panel"><div class="panel-heading"><div><p class="eyebrow">PRIVATE RECEIPTS</p><h2>Trade history</h2></div><button class="buy-btn" data-action="refresh-social">Retry</button></div><div class="empty-listings"><span>○</span><div><strong>Receipts are temporarily unavailable.</strong><p>Your completed trades remain recorded by the bazaar.</p></div></div></section>`;
  }
  return `<section class="panel trade-history-panel">
    <div class="panel-heading"><div><p class="eyebrow">PRIVATE RECEIPTS</p><h2>Recent trade history</h2></div><span class="listing-count">${social.history.length} shown</span></div>
    <div class="trade-history-list">${social.history.length
      ? social.history.map(tradeHistoryRow).join('')
      : `<div class="empty-listings"><span>◇</span><div><strong>No completed trades yet.</strong><p>Sold, purchased, and cancelled listings will leave a private receipt here.</p></div></div>`}</div>
  </section>`;
}

function tradeHistoryRow(receipt) {
  const item = ITEMS[receipt.item_id];
  const sold = receipt.trade_status === 'sold';
  const seller = receipt.trade_role === 'seller';
  const direction = !sold ? 'Returned from escrow' : seller ? 'Sold' : 'Purchased';
  const counterparty = sold
    ? `${seller ? 'to' : 'from'} ${escapeText(receipt.counterparty_name)}`
    : 'No market fee';
  const amount = !sold ? '0 ◆' : `${seller ? '+' : '−'}${money(Math.abs(receipt.net_gold))} ◆`;
  const settlement = sold && seller ? `${money(receipt.fee)} ◆ fee deducted` : sold ? 'No buyer fee' : 'Inventory restored';
  const score = sold
    ? receipt.verified_score > 0
      ? `+${money(receipt.verified_score)} weekly standing`
      : 'Daily score cap reached'
    : '';
  const receiptDetail = [settlement, score].filter(Boolean).join(' · ');
  return `<article class="trade-history-row ${receipt.trade_status} ${receipt.trade_role}">
    <span class="trade-history-mark" aria-hidden="true">${!sold ? '↩' : seller ? '◆' : '◈'}</span>
    <div class="trade-history-copy"><strong>${direction}: ${receipt.quantity}× ${item.name}</strong><span>${counterparty} · ${tradeTimestamp(receipt.completed_at)}</span></div>
    <div class="trade-history-value"><strong>${amount}</strong><span>${receiptDetail}</span></div>
  </article>`;
}

function onlineListingRow(listing) {
  const item = ITEMS[listing.item_id];
  if (!item) return '';
  const pending = pendingMutations.has(`buy-listing:${listing.id}`);
  const economics = listingEconomics(
    Number(listing.quantity),
    Number(listing.price),
    Number(listing.quantity),
    item.value,
  );
  const blocked = !economics.valid;
  const buttonLabel = pending ? 'Buying…' : blocked ? 'Price blocked' : cloud.signedIn ? 'Buy bundle' : 'Sign in';
  return `<div class="online-listing ${blocked ? 'price-blocked' : ''}"><div>${itemIcon(item)}<div><strong>${item.name}</strong><span>${listing.quantity}× · ${money(listingUnitPrice(listing))} each · by ${escapeText(listing.seller_name)}${blocked ? ' · outside appraisal band' : ''}</span></div></div><strong>${money(Number(listing.price))} ◆</strong><button class="buy-btn" data-action="buy-online-listing" data-listing="${listing.id}" ${pending || blocked ? `disabled${pending ? ' aria-busy="true"' : ' title="Outside the safe appraisal band"'}` : ''}>${buttonLabel}</button></div>`;
}

function marketRow(listing) {
  const item = ITEMS[listing.itemId];
  return `<div class="market-row"><div>${itemIcon(item)}<div><strong>${item.name}</strong><span>${item.category} · ${listing.quantity > 1 ? `x${listing.quantity}` : item.rarity}</span></div></div><div class="market-price"><strong>${money(listing.price)} <small>◆</small></strong><span>by ${listing.seller}</span></div><button class="buy-btn" data-action="buy-listing" data-listing="${listing.id}" ${state.gold >= listing.price ? '' : 'disabled'}>Buy</button></div>`;
}

function listingCard(listing) {
  const item = ITEMS[listing.itemId];
  return `<article class="listing-card">
    <div class="listing-art ${item.tone}">${item.icon}<span>${item.rarity}</span></div>
    <div class="listing-copy"><p class="eyebrow">${item.category}${listing.quantity > 1 ? ` · BUNDLE OF ${listing.quantity}` : ''}</p><h3>${item.name}</h3><p>${item.lore}</p><div class="seller-line"><span>Sold by ${listing.seller}</span><strong>${money(listing.price)} ◆</strong></div>
      <button class="button ${state.gold >= listing.price ? 'primary' : 'secondary'} full" data-action="buy-listing" data-listing="${listing.id}" ${state.gold >= listing.price ? '' : 'disabled'}>${state.gold >= listing.price ? 'Buy listing' : 'Not enough gold'}</button>
    </div>
  </article>`;
}

function stallView() {
  const offer = state.stall.offer;
  const customer = offer ? CUSTOMERS[offer.customerId] : null;
  const item = offer ? ITEMS[offer.itemId] : null;
  const fairValue = offer ? item.value * offer.quantity : 0;
  const premium = offer ? Math.round((offer.offer / fairValue - 1) * 100) : 0;
  return `${pageHeading('STALL SEVENTEEN', 'Open for peculiar business.', 'Turn dungeon finds into reputation and gold. Accept fair offers, risk a haggle, or wait for the right customer.', `<button class="button dark-button" data-tab="collection">Arrange collection <span>→</span></button>`)}
    <div class="stall-summary">
      <div><span>Stall level</span><strong>${state.stall.level}</strong><small>Lantern & oak</small></div>
      <div><span>Reputation</span><strong>${money(state.stall.reputation)}</strong><small>${stallReputation()}</small></div>
      <div><span>Customers served</span><strong>${money(state.stall.customersServed)}</strong><small>Lifetime</small></div>
      <div><span>Today’s revenue</span><strong>${money(state.stall.dailyRevenue)} ◆</strong><small>Customer sales only · no fee</small></div>
    </div>
    <div class="stall-layout">
      <section class="panel storefront-panel">
        <div class="shop-window">
          <div class="awning"><i></i><i></i><i></i><i></i><i></i></div>
          <div class="shop-sign">STALL XVII<span>RELICS · CURIOS · PROVISIONS</span></div>
          <div class="window-shelf">${state.inventory.slice(0, 4).map(entry => `<span class="${ITEMS[entry.itemId].tone}" title="${ITEMS[entry.itemId].name}">${ITEMS[entry.itemId].icon}<b>${entry.quantity}</b></span>`).join('')}</div>
          <div class="counter-bell">♢</div>
        </div>
        <div class="storefront-foot"><span><i class="live-dot"></i> Lantern lit · accepting offers</span><strong>${inventoryCount()} pieces in stock</strong></div>
      </section>
      <section class="panel customer-panel">
        ${offer ? `<div class="customer-heading"><div class="customer-avatar ${customer.tone}">${customer.icon}</div><div><p class="eyebrow">AT THE COUNTER</p><h2>${customer.name}</h2><span>${customer.title}</span></div><div class="patience" title="Patience">${[1, 2].map(point => `<i class="${point <= offer.patience ? 'full' : ''}"></i>`).join('')}</div></div>
          <blockquote>“${customer.greeting}”</blockquote>
          <div class="customer-request">${itemIcon(item)}<div><span>WANTS TO BUY</span><strong>${offer.quantity}× ${item.name}</strong><small>${item.rarity} ${item.category} · ${state.inventory.find(entry => entry.itemId === item.id)?.quantity || 0} owned</small></div></div>
          <div class="offer-price"><span>CURRENT OFFER<small>${premium >= 0 ? '+' : ''}${premium}% vs. keeper value</small></span><strong>${money(offer.offer)} <i>◆</i></strong></div>
          <div class="offer-actions"><button class="button secondary" data-action="decline-customer">Decline</button><button class="button dark-button" data-action="haggle-customer">Haggle <small>${offer.patience}/2</small></button><button class="button gold-button" data-action="accept-customer">Accept offer</button></div>`
          : `<div class="no-customer"><span>♢</span><h2>The counter is quiet.</h2><p>Bring back an item from the depths and another customer will find you.</p><button class="button primary" data-tab="dungeons">Explore a dungeon</button></div>`}
      </section>
    </div>
    <section class="panel renovation-panel">
      <div class="panel-heading"><div><p class="eyebrow">KEEPER’S WORKSHOP</p><h2>Renovate stall seventeen</h2></div><span class="renovation-level">STALL LEVEL <b>${state.stall.level}</b></span></div>
      <div class="renovation-grid">${Object.keys(RENOVATIONS).map(renovationCard).join('')}</div>
    </section>
    <section class="panel stock-panel">
      <div class="panel-heading"><div><p class="eyebrow">DISPLAY CABINET</p><h2>Stock at a glance</h2></div><button class="text-button" data-tab="collection">Manage collection ${icons.chevron}</button></div>
      <div class="stall-stock-grid">${state.inventory.slice().sort((a, b) => ITEMS[b.itemId].value - ITEMS[a.itemId].value).map(stallStockCard).join('')}</div>
    </section>`;
}

function renovationCard(renovationId) {
  const quote = renovationQuote(state, renovationId);
  const canAfford = !quote.maxed && state.gold >= quote.cost;
  return `<article class="renovation-card ${quote.maxed ? 'maxed' : ''}">
    <div class="renovation-top"><span class="renovation-icon">${quote.icon}</span><div><span>${quote.effect}</span><h3>${quote.name}</h3></div><strong>LV.${quote.level}</strong></div>
    <p>${quote.description}</p>
    <div class="renovation-ranks" role="progressbar" aria-label="${quote.name} renovation level" aria-valuemin="1" aria-valuemax="5" aria-valuenow="${quote.level}">${Array.from({ length: 5 }, (_, index) => `<i class="${index < quote.level ? 'filled' : ''}"></i>`).join('')}</div>
    <div class="renovation-effect"><span>Current</span><strong>${quote.currentBonus}</strong>${quote.nextBonus ? `<i>→</i><span>Next</span><strong>${quote.nextBonus}</strong>` : '<em>MASTERWORK</em>'}</div>
    <button class="button ${canAfford ? 'gold-button' : 'secondary'} full" data-action="upgrade-renovation" data-renovation="${quote.id}" ${quote.maxed || !canAfford ? 'disabled' : ''}>${quote.maxed ? '✓ Fully renovated' : canAfford ? `Upgrade · ${money(quote.cost)} ◆` : `Need ${money(quote.cost - state.gold)} more ◆`}</button>
  </article>`;
}

function stallStockCard(entry) {
  const item = ITEMS[entry.itemId];
  return `<div class="stall-stock-card">${itemIcon(item)}<div><strong>${item.name}</strong><span>${item.category} · ${item.rarity}</span></div><b>x${entry.quantity}</b><small>${money(item.value)} ◆ ea.</small></div>`;
}

function stallReputation() {
  if (state.stall.reputation >= 100) return 'City favorite';
  if (state.stall.reputation >= 60) return 'Well regarded';
  if (state.stall.reputation >= 30) return 'Known locally';
  return 'New on the row';
}

function collectionView() {
  const categories = ['All', 'Relic', 'Material', 'Ingredient', 'Curio'];
  const entries = Object.values(ITEMS)
    .map(item => ({
      item,
      itemId: item.id,
      quantity: state.inventory.find(entry => entry.itemId === item.id)?.quantity || 0,
      discovered: state.discoveries.includes(item.id),
    }))
    .filter(entry => collectionFilter === 'All' || entry.item.category === collectionFilter)
    .sort((a, b) => Number(b.discovered) - Number(a.discovered) || b.item.value - a.item.value);
  const completion = Math.round(discoveredCount() / Object.keys(ITEMS).length * 100);
  return `${pageHeading('PRIVATE COLLECTION', 'Treasures with a past.', 'Every expedition, draw, and trade leaves a permanent record—even after the last copy is sold.', `<button class="button gold-button" data-action="open-summon">Draw a relic · 10 ✦</button>`)}
    <div class="collection-toolbar"><div class="filter-pills" role="group" aria-label="Filter collection by category">${categories.map(category => `<button class="${collectionFilter === category ? 'active' : ''}" data-filter="${category}" aria-pressed="${collectionFilter === category}">${category}</button>`).join('')}</div><span>${inventoryCount()} owned · ${discoveredCount()} / ${Object.keys(ITEMS).length} discovered</span></div>
    <div class="codex-progress" role="progressbar" aria-label="Collection discoveries" aria-valuemin="0" aria-valuemax="${Object.keys(ITEMS).length}" aria-valuenow="${discoveredCount()}"><span style="width:${completion}%"></span><div><b>${completion}% catalogued</b><small>Unknown entries reveal a source hint</small></div></div>
    <div class="collection-grid">${entries.map(collectionCard).join('')}</div>`;
}

function collectionCard(entry) {
  const item = entry.item;
  if (!entry.discovered) {
    return `<article class="collection-card locked">
      <div class="collection-art"><span>?</span><b>UNKNOWN</b></div>
      <div class="collection-copy"><div class="rarity-line"><span>UNDISCOVERED</span><span>${item.category}</span></div><h3>Uncatalogued ${item.category}</h3><p>${discoveryHint(item.id)}</p>
        <div class="collection-actions locked-actions"><span>Keeper’s clue<strong>Record missing</strong></span><button class="buy-btn" data-tab="dungeons">Seek a find</button></div>
      </div>
    </article>`;
  }
  const sale = Math.floor(item.value * 0.72);
  const owned = entry.quantity > 0;
  const escrowFull = cloud.signedIn && social.mine.length >= 30;
  return `<article class="collection-card ${item.tone} ${owned ? '' : 'archived'}">
    <div class="collection-art"><span>${item.icon}</span><b>${owned ? `x${entry.quantity}` : 'ARCHIVED'}</b></div>
    <div class="collection-copy"><div class="rarity-line"><span>${item.rarity}</span><span>${item.category}</span></div><h3>${item.name}</h3><p>${item.lore}</p>
      <div class="collection-actions"><span>${owned ? 'Keeper offer' : 'Lifetime record'} <strong>${owned ? `${money(sale)} ◆` : 'Not currently owned'}</strong></span><div>${owned && social.mode === 'live' ? `<button class="buy-btn list-btn" data-action="open-listing" data-item="${item.id}" ${escrowFull ? 'aria-label="Check whether an escrow slot is available"' : ''}>${escrowFull ? 'Check slots' : 'List'}</button>` : ''}${owned ? `<button class="buy-btn" data-action="sell-item" data-item="${item.id}">Sell one</button>` : '<button class="buy-btn" data-tab="dungeons">Find again</button>'}</div></div>
    </div>
  </article>`;
}

function discoveryHint(itemId) {
  const routes = DUNGEONS.filter(dungeon => dungeon.loot.includes(itemId)).map(dungeon => dungeon.name);
  const reliquaryItems = new Set(['sunken-crown', 'moonlit-signet', 'ember-idol', 'glass-oracle', 'grotto-key']);
  const sources = [...routes.slice(0, 2), ...(reliquaryItems.has(itemId) ? ['the Reliquary'] : [])];
  if (!sources.length) return 'No reliable source has been recorded yet.';
  if (sources.length === 1) return `Rumored in ${sources[0]}.`;
  return `Rumored in ${sources.slice(0, -1).join(', ')} or ${sources.at(-1)}.`;
}

function leaderboardView() {
  const leaders = rankedLeaders();
  const yourIndex = leaders.findIndex(leader => leader.isYou || leader.name === 'YOU');
  const you = yourIndex + 1;
  const yourEntry = yourIndex >= 0 ? leaders[yourIndex] : null;
  const nextEntry = you > 1 ? leaders[yourIndex - 1] : null;
  const gap = nextEntry && yourEntry ? Math.max(0, nextEntry.score - yourEntry.score) : 0;
  const lifetime = Number(yourEntry?.lifetimeScore) || state.marketScore;
  const liveBoard = social.mode === 'live';
  const scoreCaption = !liveBoard
    ? 'Local preview score'
    : yourEntry
      ? 'Server-verified score'
      : cloud.signedIn
        ? 'Awaiting verified entry'
        : 'Sign in to receive a rank';
  const podium = leaders.length
    ? `${!liveBoard ? '<div class="ranking-context"><span>◇ LOCAL PRACTICE BOARD</span><p>These keeper names and scores are fictional benchmarks, not other players.</p></div>' : ''}<div class="podium">${leaders.slice(0, 3).map((leader, index) => `<div class="podium-place p${index + 1}"><span class="podium-rank">${index + 1}</span><div class="podium-avatar">${escapeInitial(leader.name)}</div><strong>${escapeText(leader.name)}</strong><span>${money(leader.score)}</span></div>`).join('')}</div>`
    : `<section class="panel ranking-empty"><span>♛</span><div><strong>No verified standings yet.</strong><p>The first server-scored trade or guild reward will open this week’s board.</p></div></section>`;
  const rankSummary = yourEntry
    ? `<strong>#${String(you).padStart(2, '0')}</strong><p>${nextEntry ? `${money(gap)} standing to pass ${escapeText(nextEntry.name)}` : 'You currently hold the gilded crown'}</p>`
    : `<strong>—</strong><p>${cloud.signedIn ? 'Your verified merchant entry has not reached this board yet.' : 'Sign in to join the verified weekly standings.'}</p>`;
  const description = liveBoard
    ? 'Weekly ranks use server-verified shared trades and guild work. Lifetime standing still records every expedition and local deal.'
    : 'Practice standings compare your local progress with fictional keeper benchmarks. Shared ranks appear only after the verified bazaar is available.';
  return `${pageHeading(liveBoard ? 'THE GILDED DESCENT · VERIFIED' : 'THE GILDED DESCENT · PRACTICE', liveBoard ? 'Hall of standing.' : 'Practice the climb.', description, socialBadge())}
    <div class="ranking-season-strip">
      <div><span>WEEKLY STANDING</span><strong>${money(yourEntry?.score || 0)}</strong><small>${scoreCaption}</small></div>
      <div><span>LIFETIME STANDING</span><strong>${money(lifetime)}</strong><small>Never resets</small></div>
      <div><span>SCORING WINDOW</span><strong data-ranking-clock>${liveBoard ? verifiedBoardCaption() : weeklyContractCaption()}</strong><small>${liveBoard ? 'Resets Monday 00:00 UTC' : 'Resets on your local Monday'}</small></div>
    </div>
    ${podium}
    ${leaders.length ? `<section class="panel ranking-table"><div class="ranking-head"><span>RANK</span><span>MERCHANT</span><span>WEEKLY STANDING</span><span>MOVEMENT</span></div>${leaders.map(leaderRow).join('')}</section>` : ''}
    <div class="your-rank-card"><span>${liveBoard ? 'YOUR VERIFIED RANK' : 'YOUR PREVIEW RANK'}</span>${rankSummary}</div>`;
}

function rankedLeaders() {
  const shared = social.mode === 'live'
    ? social.leaders.map(leader => ({
      name: leader.display_name,
      score: Number(leader.weekly_score) || 0,
      mark: `${Number(leader.discoveries) || 0} finds`,
      lifetimeScore: Number(leader.lifetime_score) || 0,
      tone: leader.is_you ? 'you' : '',
      isYou: Boolean(leader.is_you),
    }))
    : null;
  const base = shared || LEADERS.map(leader => leader.name === 'YOU' ? { ...leader, score: weeklyStanding(state), isYou: true } : leader);
  return base
    .sort((a, b) => b.score - a.score)
    .map((leader, index) => ({ ...leader, rank: index + 1 }));
}

function leaderRow(leader) {
  return `<div class="leader-row ${leader.isYou || leader.name === 'YOU' ? 'you' : ''}"><span class="rank ${leader.tone}">${String(leader.rank).padStart(2, '0')}</span><span class="leader-avatar ${leader.tone}">${escapeInitial(leader.name)}</span><strong>${escapeText(leader.name)}</strong><span class="leader-score">${money(leader.score)}</span><span class="leader-badge">${escapeText(leader.mark)}</span></div>`;
}

function guildView() {
  const completed = Math.min(WEEKLY_CONTRACT.target, state.weekly.expeditions);
  const percent = Math.round(completed / WEEKLY_CONTRACT.target * 100);
  const ready = completed >= WEEKLY_CONTRACT.target;
  const claimedCount = DAILY_COMMISSIONS.filter(commission => state.dailyCommissions.claimed.includes(commission.id)).length;
  const allClaimed = claimedCount === DAILY_COMMISSIONS.length;
  const chronicleReady = achievementReadyCount();
  const nextAchievement = ACHIEVEMENTS.find(achievement => !state.achievements.unlocked.includes(achievement.id));
  const nextProgress = nextAchievement ? achievementProgress(state, nextAchievement) : null;
  const directoryAvailable = cloud.signedIn && social.mode === 'live' && !social.rosterError;
  const directoryCount = directoryAvailable ? social.roster.length : null;
  const campaignMarks = social.campaign && !social.campaignError
    ? Math.max(0, Number(social.campaign.total_points) || 0)
    : null;
  const guildDescription = directoryAvailable
    ? `${directoryCount} authenticated merchant ledger${directoryCount === 1 ? ' is' : 's are'} visible in the current directory. Weekly contracts and the shared caravan update from verified records.`
    : cloud.configured
      ? 'Local contracts remain available while the authenticated merchant directory and shared caravan connect.'
      : 'Local contracts and the Keeper’s Chronicle remain fully playable without the shared guild service.';
  const bannerKicker = directoryAvailable ? 'SHARED GUILD · VERIFIED DIRECTORY' : 'LOCAL GUILD CHAPTER';
  const metricLabel = campaignMarks !== null ? 'WEEKLY ROUTE MARKS' : directoryCount !== null ? 'DIRECTORY SHOWN' : 'SHARED STATUS';
  const metricValue = campaignMarks !== null ? money(campaignMarks) : directoryCount !== null ? money(directoryCount) : cloud.signedIn ? 'SYNCING' : 'SIGN IN';
  return `${pageHeading('COPPER & CLOVER', 'A guild built on good finds.', guildDescription, `<button class="button dark-button" data-action="open-achievements">Keeper’s Chronicle ${chronicleReady ? `<b class="button-badge">${chronicleReady}</b>` : '<span>→</span>'}</button>`)}
    <div class="guild-banner"><div class="guild-crest">♜</div><div><span>${bannerKicker}</span><h2>Copper & Clover</h2><p>“Leave no shelf empty and no adventurer unpaid.”</p></div><div class="guild-rank"><span>${metricLabel}</span><strong>${metricValue}</strong></div></div>
    ${campaignPanel()}
    <section class="panel chronicle-preview">
      <div class="chronicle-preview-mark">♜</div>
      <div><p class="eyebrow">KEEPER’S CHRONICLE · PERMANENT</p><h2>${chronicleReady ? `${chronicleReady} sealed reward${chronicleReady === 1 ? '' : 's'} waiting` : nextAchievement ? nextAchievement.title : 'Every entry is written'}</h2><p>${chronicleReady ? 'Your lifetime deeds have opened new Chronicle seals.' : nextAchievement ? nextAchievement.description : 'Stall seventeen’s complete record belongs in the guild archive.'}</p>
        <div class="chronicle-preview-progress"><i style="width:${nextProgress?.percent ?? 100}%"></i><span>${nextAchievement ? `${nextProgress.current.toLocaleString()} / ${nextProgress.target.toLocaleString()} ${nextAchievement.unit}` : `${ACHIEVEMENTS.length} / ${ACHIEVEMENTS.length} achievements`}</span></div>
      </div>
      <button class="button ${chronicleReady ? 'gold-button' : 'secondary'}" data-action="open-achievements">${chronicleReady ? 'Open rewards' : 'View Chronicle'}</button>
    </section>
    <section class="panel commission-board">
      <div class="panel-heading"><div><p class="eyebrow">DAILY COMMISSIONS</p><h2>Keeper’s order board</h2></div><span class="streak-badge"><b>${claimedCount}</b> / ${DAILY_COMMISSIONS.length} claimed · <i data-market-clock>${marketBellCaption()}</i></span></div>
      <div class="commission-grid">${DAILY_COMMISSIONS.map(commissionCard).join('')}</div>
      <div class="commission-seal ${allClaimed ? 'ready' : ''} ${state.dailyCommissions.bonusClaimed ? 'claimed' : ''}">
        <div class="seal-mark">${state.dailyCommissions.bonusClaimed ? '✓' : '◆'}</div>
        <div><span>COMPLETE THE BOARD</span><strong>${state.dailyCommissions.bonusClaimed ? 'Ledger sealed for today' : 'Seal the keeper’s ledger'}</strong><p>${DAILY_COMMISSION_BONUS.gems} gems · ${money(DAILY_COMMISSION_BONUS.marketScore)} standing · ${DAILY_COMMISSION_BONUS.xp} XP</p></div>
        <button class="button ${allClaimed && !state.dailyCommissions.bonusClaimed ? 'gold-button' : 'secondary'}" data-action="claim-commission-bonus" ${allClaimed && !state.dailyCommissions.bonusClaimed ? '' : 'disabled'}>${state.dailyCommissions.bonusClaimed ? '✓ Sealed' : allClaimed ? 'Seal ledger' : `${DAILY_COMMISSIONS.length - claimedCount} unclaimed`}</button>
      </div>
    </section>
    <div class="guild-layout"><section class="panel contract-panel"><div class="panel-heading"><div><p class="eyebrow">WEEKLY CONTRACT</p><h2>Three bells beyond the gate</h2></div><span class="streak-badge" data-weekly-clock>${weeklyContractCaption()}</span></div><div class="contract-body"><div class="contract-progress" role="progressbar" aria-label="Weekly expedition contract" aria-valuemin="0" aria-valuemax="${WEEKLY_CONTRACT.target}" aria-valuenow="${completed}"><span style="width:${percent}%"></span></div><div class="contract-meta"><span>Complete ${WEEKLY_CONTRACT.target} expeditions</span><strong>${completed} / ${WEEKLY_CONTRACT.target}</strong></div><div class="contract-reward-row"><p>Reward: ${WEEKLY_CONTRACT.gems} gems · ${money(WEEKLY_CONTRACT.marketScore)} standing · ${WEEKLY_CONTRACT.xp} XP</p><button class="button ${ready && !state.weekly.claimed ? 'gold-button' : 'secondary'}" data-action="claim-weekly" ${ready && !state.weekly.claimed ? '' : 'disabled'}>${state.weekly.claimed ? '✓ Reward claimed' : ready ? 'Claim contract' : `${WEEKLY_CONTRACT.target - completed} run${WEEKLY_CONTRACT.target - completed === 1 ? '' : 's'} remaining`}</button></div></div></section>
    <section class="panel journal-panel"><div class="panel-heading"><div><p class="eyebrow">STALL JOURNAL</p><h2>Recent activity</h2></div></div><div class="journal-list">${state.journal.map(entry => `<div><span aria-hidden="true">◆</span><p>${escapeText(entry)}</p></div>`).join('')}</div></section></div>`;
}

function campaignPanel() {
  const heading = `<div class="campaign-heading"><div><p class="eyebrow">WEEKLY CO-OP EXPEDITION</p><h2>Lanternwake: The Hollow Regent</h2><p>Every signed-in merchant may dispatch one caravan each day. The whole guild opens the same reward caches.</p></div><span class="campaign-clock"><i class="live-dot"></i> <b data-weekly-clock>${weeklyContractCaption()}</b></span></div>`;
  if (!cloud.configured) {
    return `<section class="panel campaign-panel campaign-locked">${heading}<div class="campaign-gate"><span>♜</span><div><strong>The shared road is not yet mapped.</strong><p>Apply the included Dungeon Market schema to open cooperative expeditions. Your local stall remains fully playable.</p></div></div></section>`;
  }
  if (!cloud.signedIn) {
    return `<section class="panel campaign-panel campaign-locked">${heading}<div class="campaign-gate"><span>◇</span><div><strong>Join the guild caravan.</strong><p>Use your shared Melodia account to contribute alongside other keepers and claim communal rewards.</p></div><button class="button primary" data-action="open-account">Sign in to dispatch</button></div></section>`;
  }
  if (social.mode === 'connecting' && !social.campaign) {
    return `<section class="panel campaign-panel">${heading}<div class="campaign-loading"><span class="cloud-spinner"></span><p>Reading the guild’s expedition ledger…</p></div></section>`;
  }
  if (social.campaignError || !social.campaign) {
    return `<section class="panel campaign-panel campaign-locked">${heading}<div class="campaign-gate"><span>○</span><div><strong>The expedition ledger is out of reach.</strong><p>No contribution was lost. Reconnect to read the guild’s current progress.</p></div><button class="button secondary" data-action="refresh-social">Try again</button></div></section>`;
  }
  const campaign = social.campaign;
  const total = Math.max(0, Number(campaign.total_points) || 0);
  const personal = Math.max(0, Number(campaign.your_points) || 0);
  const contributors = Math.max(0, Number(campaign.contributors) || 0);
  const claimed = new Set((Array.isArray(campaign.claimed_tiers) ? campaign.claimed_tiers : []).map(Number));
  const finalTarget = CAMPAIGN_TIERS.at(-1).target;
  const percent = Math.min(100, Math.round(total / finalTarget * 100));
  const dispatched = Boolean(campaign.contributed_today);
  const dispatchPending = pendingMutations.has('campaign-contribute');
  return `<section class="panel campaign-panel">
    ${heading}
    <div class="campaign-scene">
      <div class="campaign-sigil"><i></i><span>♜</span></div>
      <div class="campaign-copy"><span>COMMUNITY PROGRESS</span><strong>${money(total)} <small>/ ${money(finalTarget)} route marks</small></strong><p>${contributors} keeper${contributors === 1 ? '' : 's'} on the road · your caravans contributed ${personal} marks</p></div>
      <button class="button ${dispatched ? 'secondary' : 'gold-button'}" data-action="campaign-contribute" ${dispatched || dispatchPending ? 'disabled' : ''} ${dispatchPending ? 'aria-busy="true"' : ''}>${dispatchPending ? 'Dispatching…' : dispatched ? '✓ Caravan sent today' : 'Dispatch caravan · +10'}</button>
    </div>
    <div class="campaign-track" role="progressbar" aria-label="Lanternwake community progress" aria-valuemin="0" aria-valuemax="${finalTarget}" aria-valuenow="${Math.min(total, finalTarget)}"><i style="width:${percent}%"></i>${CAMPAIGN_TIERS.map(tier => `<span style="left:${tier.target / finalTarget * 100}%" class="${total >= tier.target ? 'reached' : ''}"></span>`).join('')}</div>
    <div class="campaign-tiers">${CAMPAIGN_TIERS.map(tier => campaignTierCard(tier, total, claimed)).join('')}</div>
  </section>`;
}

function campaignTierCard(tier, total, claimed) {
  const unlocked = total >= tier.target;
  const isClaimed = claimed.has(tier.tier);
  const pending = pendingMutations.has(`campaign-claim:${tier.tier}`);
  return `<article class="campaign-tier ${unlocked ? 'unlocked' : ''} ${isClaimed ? 'claimed' : ''}">
    <span class="campaign-tier-mark">${isClaimed ? '✓' : tier.tier}</span>
    <div><small>${money(tier.target)} MARKS</small><strong>${tier.title}</strong><p>${money(tier.reward.gold)} ◆ · ${tier.reward.gems} ✦ · ${money(tier.reward.marketScore)} standing</p></div>
    <button class="buy-btn" data-action="campaign-claim" data-tier="${tier.tier}" ${unlocked && !isClaimed && !pending ? '' : 'disabled'} ${pending ? 'aria-busy="true"' : ''}>${pending ? 'Claiming…' : isClaimed ? 'Claimed' : unlocked ? 'Claim' : `${money(Math.max(0, tier.target - total))} left`}</button>
  </article>`;
}

function commissionCard(commission) {
  const progress = Math.min(commission.target, state.dailyCommissions.progress[commission.metric] || 0);
  const percent = Math.round(progress / commission.target * 100);
  const claimed = state.dailyCommissions.claimed.includes(commission.id);
  const ready = progress >= commission.target;
  const rewards = [
    commission.reward.gold ? `${money(commission.reward.gold)} ◆` : '',
    commission.reward.gems ? `${commission.reward.gems} ✦` : '',
    `${commission.reward.xp} XP`,
  ].filter(Boolean).join(' · ');
  return `<article class="commission-card ${ready ? 'ready' : ''} ${claimed ? 'claimed' : ''}">
    <div class="commission-icon">${claimed ? '✓' : commission.icon}</div>
    <div class="commission-copy"><span>${claimed ? 'COMMISSION CLAIMED' : rewards}</span><h3>${commission.title}</h3><p>${commission.description}</p></div>
    <div class="commission-progress" role="progressbar" aria-label="${commission.title}" aria-valuemin="0" aria-valuemax="${commission.target}" aria-valuenow="${progress}"><i style="width:${percent}%"></i></div>
    <div class="commission-foot"><strong>${progress} / ${commission.target}</strong><button class="buy-btn" data-action="claim-commission" data-commission="${commission.id}" ${ready && !claimed ? '' : 'disabled'}>${claimed ? 'Claimed' : ready ? 'Claim' : 'In progress'}</button></div>
  </article>`;
}

function friendsView() {
  const heading = pageHeading(
    'YOUR CIRCLE',
    'Keep a lantern lit.',
    'Browse authenticated merchant ledgers and send one guild lantern to each keeper every day. Activity timestamps describe ledger updates, not live presence.',
    socialBadge(),
  );
  if (!cloud.configured) {
    return `${heading}<section class="panel social-gate"><div class="social-gate-mark">♧</div><div><p class="eyebrow">LOCAL LEDGER</p><h2>Your circle opens with the shared bazaar.</h2><p>Dungeon Market remains fully playable offline. Apply the included Supabase schema to enable authenticated merchant profiles and guild lanterns.</p></div></section>`;
  }
  if (!cloud.signedIn) {
    return `${heading}<section class="panel social-gate"><div class="social-gate-mark">◇</div><div><p class="eyebrow">MERCHANT ACCOUNT</p><h2>Sign in to meet the guild.</h2><p>Your Melodia account connects the same identity used by Survivor and Total Defence. No local progress is removed.</p><button class="button primary" data-action="open-account">Open shared ledger <span>→</span></button></div></section>`;
  }
  if (social.mode === 'connecting') {
    return `${heading}<section class="panel social-gate loading-social"><span class="cloud-spinner"></span><div><h2>Lighting the guild hall…</h2><p>Fetching current merchant ledgers and today’s lanterns.</p></div></section>`;
  }
  if (social.rosterError) {
    return `${heading}<section class="panel social-gate"><div class="social-gate-mark">○</div><div><p class="eyebrow">GUILD HALL UNREACHABLE</p><h2>Your local stall is still open.</h2><p>The social roster could not be reached. Trading, expeditions, and offline progress remain safe.</p><button class="button secondary" data-action="refresh-social">Try again</button></div></section>`;
  }
  const me = social.roster.find(merchant => merchant.is_you);
  const merchants = social.roster.filter(merchant => !merchant.is_you);
  const sent = merchants.filter(merchant => merchant.lantern_sent_today).length;
  return `${heading}
    <div class="circle-summary">
      <div><span>DIRECTORY ENTRIES</span><strong>${merchants.length}</strong><small>Other authenticated ledgers shown</small></div>
      <div><span>YOUR WEEKLY LANTERNS</span><strong>${Number(me?.lanterns_this_week) || 0}</strong><small>Sent by other keepers</small></div>
      <div><span>TODAY’S SIGNALS</span><strong>${sent} / ${merchants.length}</strong><small>${merchants.length - sent} lantern${merchants.length - sent === 1 ? '' : 's'} still available</small></div>
    </div>
    <section class="panel friends-panel">
      <div class="panel-heading"><div><p class="eyebrow">MERCHANT DIRECTORY</p><h2>The guild ledger</h2></div><span class="market-clock"><i class="live-dot"></i> server verified</span></div>
      ${merchants.length ? merchants.map(merchantRow).join('') : `<div class="circle-empty"><span>♧</span><div><strong>No other merchant ledgers are listed yet.</strong><p>New authenticated keepers will appear after their first Dungeon Market sync.</p></div></div>`}
    </section>`;
}

function merchantRow(merchant, index) {
  const sent = Boolean(merchant.lantern_sent_today);
  const name = String(merchant.display_name || 'Merchant');
  const finds = Math.max(0, Number(merchant.discoveries) || 0);
  const lanterns = Math.max(0, Number(merchant.lanterns_this_week) || 0);
  const pending = pendingMutations.has(`send-lantern:${merchant.social_id}`);
  return `<div class="friend-row ${sent ? 'lantern-sent' : ''}">
    <div class="friend-rank">${String(index + 1).padStart(2, '0')}</div>
    <div class="friend-avatar">${escapeInitial(name)}</div>
    <div class="friend-copy"><strong>${escapeText(name)}</strong><span>${ledgerRecency(merchant.updated_at)} · ${finds} discover${finds === 1 ? 'y' : 'ies'}</span></div>
    <div class="friend-standing"><strong>${money(Number(merchant.market_score) || 0)}</strong><span>standing</span></div>
    <div class="lantern-count"><b>◇ ${lanterns}</b><span>this week</span></div>
    <button class="buy-btn lantern-button" data-action="send-lantern" data-social-id="${merchant.social_id}" data-merchant="${escapeText(name)}" ${sent || pending ? 'disabled' : ''} ${pending ? 'aria-busy="true"' : ''}>${pending ? 'Lighting…' : sent ? '✓ Lantern sent' : 'Send lantern'}</button>
  </div>`;
}

function modalView() {
  if (modal === 'onboarding') return onboardingModal();
  if (modal === 'account') return accountModal();
  if (modal === 'restore-save') return restoreSaveModal();
  if (modal === 'cloud-conflict') return cloudConflictModal();
  if (modal === 'achievements') return achievementModal();
  if (modal === 'market-path') return marketPathModal();
  if (modal === 'return-report') return returnReportModal();
  if (modal === 'expedition-result') return expeditionResultModal();
  if (modal === 'dungeon') {
    return `<div class="modal-backdrop" data-action="close-modal"><div class="modal" data-modal-panel>
      <div class="modal-head"><div><p class="eyebrow">CHOOSE YOUR DEPTH</p><h2>Dungeon board</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
      ${partyBuilder(true)}
      <div class="modal-dungeons">${DUNGEONS.map(dungeon => {
        const forecast = expeditionForecast(state, dungeon.id);
        return `<div class="modal-dungeon"><div class="dungeon-card-icon ${dungeon.tone}">${dungeon.icon}</div><div class="modal-dungeon-copy"><h3>${dungeon.name}</h3><p>${Math.round(forecast.successChance * 100)}% ${forecast.rating} · ${dungeon.risk} risk · ${duration(expeditionDuration(state, dungeon))}</p><span>Base gold before crew perks <strong>${money(dungeon.rewardMin)}–${money(dungeon.rewardMax)} ◆</strong></span></div><button class="button ${state.energy >= dungeon.cost && !state.expedition ? 'primary' : 'disabled'}" data-action="start-expedition" data-dungeon="${dungeon.id}" ${state.energy >= dungeon.cost && !state.expedition ? '' : 'disabled'}>${dungeon.cost} ϟ · Enter</button></div>`;
      }).join('')}</div>
    </div></div>`;
  }
  if (modal === 'reset') {
    return `<div class="modal-backdrop" data-action="close-modal"><div class="modal confirm-modal" data-modal-panel><div class="modal-head"><div><p class="eyebrow">KEEPER’S LEDGER</p><h2>Reset local progress?</h2></div><button class="icon-btn" data-action="close-modal">×</button></div><p>This clears this browser’s purse, collection, expedition, and streak. It cannot be undone.</p><div class="confirm-actions"><button class="button secondary" data-action="close-modal">Keep progress</button><button class="button danger-button" data-action="confirm-reset">Reset game</button></div></div></div>`;
  }
  if (modal === 'listing') {
    const item = ITEMS[listingItemId];
    const owned = state.inventory.find(entry => entry.itemId === listingItemId)?.quantity || 0;
    const publishing = pendingMutations.has('create-listing');
    const maxQuantity = Math.max(1, Math.min(999, owned));
    const quantity = Math.max(1, Math.min(maxQuantity, Math.round(Number(listingDraftQuantity) || 1)));
    const price = Math.max(1, Math.round(Number(listingDraftPrice) || item.value * quantity));
    const median = liveUnitMedian(item.id);
    const guide = Math.max(1, Math.round(median || item.value));
    const economics = listingEconomics(quantity, price, owned, item.value);
    const guideUnit = Math.max(
      Math.ceil(economics.minPrice / quantity),
      Math.min(Math.floor(economics.maxPrice / quantity), guide),
    );
    return `<div class="modal-backdrop" data-action="close-modal"><div class="modal listing-modal" data-modal-panel>
      <div class="modal-head"><div><p class="eyebrow">SHARED BAZAAR · SECURE ESCROW</p><h2>List ${item.name}</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
      <div class="listing-item-summary">${itemIcon(item)}<div><strong>${item.name}</strong><span>${item.rarity} ${item.category} · ${owned} owned</span></div></div>
      <div class="listing-form-grid">
        <label class="price-field" for="listing-quantity"><span>Bundle quantity</span><div><input id="listing-quantity" type="number" min="1" max="${maxQuantity}" step="1" value="${quantity}" inputmode="numeric" data-autofocus ${publishing ? 'disabled' : ''}><b>/ ${owned}</b></div><small>The full bundle is held in escrow.</small></label>
        <label class="price-field" for="listing-price"><span>Total asking price</span><div><input id="listing-price" type="number" min="${economics.minPrice}" max="${economics.maxPrice}" step="1" value="${price}" inputmode="numeric" ${publishing ? 'disabled' : ''}><b>◆</b></div><small>Safe range: ${money(economics.minPrice)}–${money(economics.maxPrice)} ◆.</small></label>
      </div>
      <div class="price-intelligence">
        <div><span>APPRAISAL</span><strong>${money(item.value)} ◆ <small>each</small></strong></div>
        <div><span>LIVE MEDIAN</span><strong>${median ? `${money(median)} ◆` : 'No listings'} <small>${median ? 'each' : 'yet'}</small></strong></div>
        <div><span>YOU RECEIVE</span><strong data-listing-net>${money(economics.net)} ◆</strong><small data-listing-unit>${money(economics.unitPrice)} each · ${money(economics.fee)} fee</small></div>
      </div>
      <div class="price-suggestions"><span>PRICE THE BUNDLE AT</span><button class="buy-btn" data-action="listing-price-suggestion" data-unit-price="${Math.max(Math.ceil(economics.minPrice / quantity), Math.round(guideUnit * .9))}">Quick sale · ${money(Math.max(Math.ceil(economics.minPrice / quantity), guideUnit * .9))} ea.</button><button class="buy-btn selected" data-action="listing-price-suggestion" data-unit-price="${guideUnit}">Market · ${money(guideUnit)} ea.</button><button class="buy-btn" data-action="listing-price-suggestion" data-unit-price="${Math.min(Math.floor(economics.maxPrice / quantity), Math.round(guideUnit * 1.1))}">Patient · ${money(Math.min(Math.floor(economics.maxPrice / quantity), guideUnit * 1.1))} ea.</button></div>
      <button class="button primary full" data-action="create-online-listing" ${publishing ? 'disabled aria-busy="true"' : ''}>${publishing ? 'Publishing…' : `Publish ${quantity}× bundle <span>→</span>`}</button>
    </div></div>`;
  }
  if (modal === 'summon-result') {
    const legendary = summonResult.filter(reward => reward.rarity === 'Legendary').length;
    const epic = summonResult.filter(reward => reward.rarity === 'Epic').length;
    const newFinds = summonResult.filter(reward => reward.newDiscovery).length;
    const guaranteed = summonResult.filter(reward => reward.guaranteed).length;
    const repeatCount = summonResult.length === 10 ? 10 : 1;
    const repeatCost = repeatCount * 10;
    return `<div class="modal-backdrop reliquary-backdrop" data-action="close-modal"><div class="modal summon-result-modal ${summonResult.length === 1 ? 'single-result' : ''}" data-modal-panel>
      <div class="result-radiance"></div>
      <div class="modal-head"><div><p class="eyebrow">THE RELIQUARY OPENS${guaranteed ? ' · FATE ANSWERED' : ''}</p><h2>${summonResult.length === 1 ? 'A story revealed.' : 'Ten stories revealed.'}</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
      <div class="summon-results">${summonResult.map((reward, index) => summonResultCard(reward, index)).join('')}</div>
      <div class="result-summary"><span>${legendary ? `${legendary} Legendary` : epic ? `${epic} Epic` : 'Rare collection finds'}${newFinds ? ` · ${newFinds} new` : ''}</span><strong>${state.gems} ✦ remaining · ${money(state.summonStats.total)} lifetime draws</strong></div>
      <div class="result-actions"><button class="button secondary" data-action="return-summon">Reliquary details</button><button class="button gold-button" data-action="draw-relic" data-count="${repeatCount}" ${state.gems < repeatCost ? 'disabled' : ''}>${repeatCount === 10 ? 'Reveal ten again' : 'Draw again'} · ${repeatCost} ✦</button></div>
    </div></div>`;
  }
  return `<div class="modal-backdrop" data-action="close-modal"><div class="modal reliquary-modal" data-modal-panel>
    <div class="modal-head"><div><p class="eyebrow">THE RELIQUARY</p><h2>Draw a relic</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
    <div class="reliquary-layout"><div class="reliquary-altar"><div class="altar-rings"><i></i><i></i><i></i></div><div class="summon-art">♕</div><span>SEALED COLLECTION IV</span></div>
      <div class="reliquary-copy"><p class="summon-copy">Every draw uncovers a tradeable relic. Duplicates stack in your collection; no result is discarded.</p>
        <div class="pity-heading"><span>LEGENDARY GUARANTEE</span><strong>${10 - state.pity} draw${10 - state.pity === 1 ? '' : 's'} away</strong></div>
        <div class="pity-track">${Array.from({ length: 10 }, (_, index) => `<i class="${index < state.pity ? 'filled' : index === 9 ? 'guarantee' : ''}">${index === 9 ? '♛' : ''}</i>`).join('')}</div>
        <p class="odds-note">Base odds before the fate guarantee</p>
        <div class="odds"><span><i class="dot orange"></i> Legendary <b>2%</b></span><span><i class="dot violet"></i> Epic <b>12%</b></span><span><i class="dot gray"></i> Rare <b>86%</b></span></div>
        <div class="tenfold-guarantee"><span>♛</span><div><strong>Tenfold guarantee</strong><p>Every ten-draw contains at least one Legendary, even if your fate counter begins empty.</p></div></div>
        <div class="summon-buttons"><button class="button dark-button" data-action="draw-relic" data-count="1" ${state.gems < 10 ? 'disabled' : ''}>Draw 1 <span>10 ✦</span></button><button class="button gold-button" data-action="draw-relic" data-count="10" ${state.gems < 100 ? 'disabled' : ''}>Draw 10 <span>100 ✦ · Legendary guaranteed</span></button></div>
        <p class="currency-note">You have <strong>${state.gems} ✦</strong>. Gems are earned through play; Dungeon Market does not sell gems or relic draws.</p>
      </div>
    </div>
    ${summonHistory()}
  </div></div>`;
}

function achievementModal() {
  const ready = achievementReadyCount();
  const completed = state.achievements.unlocked.length;
  const claimed = state.achievements.claimed.length;
  return `<div class="modal-backdrop chronicle-backdrop" data-action="close-modal"><div class="modal chronicle-modal" data-modal-panel>
    <div class="chronicle-radiance"></div>
    <div class="modal-head"><div><p class="eyebrow">PERMANENT KEEPER RECORD</p><h2>Keeper’s Chronicle</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
    <div class="chronicle-summary">
      <div class="chronicle-seal"><i></i><span>♜</span></div>
      <div><span>STALL XVII · LIFETIME RECORD</span><strong>${completed} of ${ACHIEVEMENTS.length} entries written</strong><div class="chronicle-summary-bar" role="progressbar" aria-label="Chronicle completion" aria-valuemin="0" aria-valuemax="${ACHIEVEMENTS.length}" aria-valuenow="${completed}"><i style="width:${Math.round(completed / ACHIEVEMENTS.length * 100)}%"></i></div><p>These entries never reset with the market bell or season. Break each seal once to claim its reward.</p></div>
      <div class="chronicle-ready ${ready ? 'ready' : ''}"><strong>${ready}</strong><span>READY</span></div>
    </div>
    <div class="chronicle-grid">${ACHIEVEMENTS.map(achievementCard).join('')}</div>
    <div class="chronicle-foot"><span>${claimed} rewards claimed · ${completed} entries unlocked</span><div><button class="button secondary" data-action="close-modal">Close chronicle</button><button class="button gold-button" data-action="claim-achievements-all" ${ready ? 'data-autofocus' : 'disabled'}>${ready ? `Claim all ${ready}` : 'No rewards ready'} <span>→</span></button></div></div>
  </div></div>`;
}

function achievementCard(achievement) {
  const progress = achievementProgress(state, achievement);
  const reward = [
    achievement.reward.gold ? `${money(achievement.reward.gold)} ◆` : '',
    achievement.reward.gems ? `${achievement.reward.gems} ✦` : '',
  ].filter(Boolean).join(' · ');
  return `<article class="chronicle-card ${progress.unlocked ? 'unlocked' : 'locked'} ${progress.claimed ? 'claimed' : ''}">
    <div class="chronicle-card-icon ${achievement.tone}">${progress.claimed ? '✓' : achievement.icon}</div>
    <div class="chronicle-card-copy"><span>${progress.claimed ? 'REWARD CLAIMED' : progress.unlocked ? `${reward} READY` : reward}</span><h3>${achievement.title}</h3><p>${achievement.description}</p></div>
    <div class="chronicle-progress"><i style="width:${progress.percent}%"></i></div>
    <div class="chronicle-card-foot"><span>${progress.current.toLocaleString()} / ${progress.target.toLocaleString()} ${achievement.unit}</span><button class="buy-btn" data-action="claim-achievement" data-achievement="${achievement.id}" ${progress.unlocked && !progress.claimed ? '' : 'disabled'}>${progress.claimed ? 'Claimed' : progress.unlocked ? 'Break seal' : 'Locked'}</button></div>
  </article>`;
}

function cloudConflictModal() {
  if (!cloudConflict) return '';
  const device = ledgerSnapshot(state);
  const server = cloudConflict.server;
  const deviceItems = Object.values(device.inventory).reduce((total, quantity) => total + quantity, 0);
  const serverItems = Object.values(server.inventory).reduce((total, quantity) => total + quantity, 0);
  return `<div class="modal-backdrop cloud-conflict-backdrop"><div class="modal cloud-conflict-modal" data-modal-panel>
    <div class="cloud-conflict-mark"><i></i><span>◇</span></div>
    <div class="cloud-conflict-heading"><p class="eyebrow">TWO KEEPER’S LEDGERS FOUND</p><h2>Which progress should lead?</h2><p>This device and your cloud account have different trade balances. Choose once; future gains will reconcile automatically without replacing remote sale proceeds.</p></div>
    <div class="ledger-choice-grid">
      <article><span>THIS DEVICE</span><h3>Keep local progress</h3><div><p>Gold<strong>${money(device.gold)} ◆</strong></p><p>Gems<strong>${device.gems} ✦</strong></p><p>Standing<strong>${money(device.marketScore)}</strong></p><p>Stock<strong>${deviceItems} items</strong></p></div><button class="button dark-button full" data-action="cloud-use-device">Use this device</button></article>
      <div class="ledger-choice-or">OR</div>
      <article class="cloud-choice"><span>CLOUD ACCOUNT</span><h3>Use online ledger</h3><div><p>Gold<strong>${money(server.gold)} ◆</strong></p><p>Gems<strong>${server.gems} ✦</strong></p><p>Standing<strong>${money(server.marketScore)}</strong></p><p>Stock<strong>${serverItems} items</strong></p></div><button class="button gold-button full" data-action="cloud-use-server" data-autofocus>Use cloud ledger</button></article>
    </div>
    <p class="cloud-conflict-note">Your codex, adventurers, renovations, and Market Path stay on this device either way. Only the shared trade ledger is reconciled.</p>
  </div></div>`;
}

function marketPathModal() {
  const progress = marketLevelProgress(state);
  const ready = marketPathReadyCount();
  return `<div class="modal-backdrop market-path-backdrop" data-action="close-modal"><div class="modal market-path-modal" data-modal-panel>
    <div class="path-radiance"></div>
    <div class="modal-head"><div><p class="eyebrow">SEASON 04 · FREE MARKET PATH</p><h2>The Gilded Descent</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
    <div class="path-overview">
      <div class="path-level-seal"><span>${state.level}</span><small>MARKET<br>LEVEL</small></div>
      <div class="path-progress-copy"><span>${state.level >= 50 ? 'THE DESCENT IS COMPLETE' : `NEXT LEVEL · ${progress.remaining} XP REMAINING`}</span><strong>${progress.xp.toLocaleString()} market XP</strong><div class="path-progress-bar" role="progressbar" aria-label="Progress to next Market level" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${progress.percent}"><i style="width:${progress.percent}%"></i></div><p>Earn XP through expeditions, customer sales, trading, renovations, and guild work. Every reward is earned through play.</p></div>
      <div class="path-ready-count ${ready ? 'ready' : ''}"><strong>${ready}</strong><span>REWARD${ready === 1 ? '' : 'S'} READY</span></div>
    </div>
    <div class="path-track" aria-label="Market Path rewards">
      ${MARKET_PATH_REWARDS.map(marketPathRewardCard).join('')}
    </div>
    <div class="path-foot"><span>${state.marketPath.claimed.length} / ${MARKET_PATH_REWARDS.length} seals claimed</span><div><button class="button secondary" data-action="close-modal">Return to bazaar</button><button class="button gold-button" data-action="claim-market-path-all" ${ready ? 'data-autofocus' : 'disabled'}>${ready ? `Claim all ${ready}` : 'No rewards ready'} <span>→</span></button></div></div>
  </div></div>`;
}

function marketPathRewardCard(reward) {
  const claimed = state.marketPath.claimed.includes(reward.level);
  const available = state.level >= reward.level && !claimed;
  const locked = state.level < reward.level;
  let icon;
  let label;
  let detail;
  if (reward.type === 'gold') {
    icon = '◆';
    label = `${money(reward.amount)} gold`;
    detail = 'Keeper’s purse';
  } else if (reward.type === 'gems') {
    icon = '✦';
    label = `${reward.amount} gems`;
    detail = 'Reliquary currency';
  } else {
    const item = ITEMS[reward.itemId];
    icon = item.icon;
    label = `${reward.quantity}× ${item.name}`;
    detail = `${item.rarity} ${item.category}`;
  }
  return `<article class="path-reward-card ${available ? 'available' : ''} ${claimed ? 'claimed' : ''} ${locked ? 'locked' : ''}">
    <div class="path-reward-level"><span>LEVEL</span><strong>${reward.level}</strong></div>
    <div class="path-reward-art ${reward.type}"><i>${claimed ? '✓' : icon}</i></div>
    <div class="path-reward-copy"><strong>${claimed ? 'Seal claimed' : label}</strong><span>${claimed ? label : detail}</span></div>
    <button class="path-claim-button" data-action="claim-market-path" data-reward-level="${reward.level}" ${available ? '' : 'disabled'}>${claimed ? 'CLAIMED' : locked ? `${reward.level - state.level} LEVELS TO GO` : 'BREAK SEAL'}</button>
  </article>`;
}

function returnReportModal() {
  if (!returnReport) return '';
  const dungeon = DUNGEONS.find(entry => entry.id === returnReport.dungeonId);
  const ledgerEntries = [
    returnReport.energyGained
      ? `<div class="return-ledger-entry energy"><span>ϟ</span><div><small>ENERGY RECOVERED</small><strong>+${returnReport.energyGained}</strong><p>${returnReport.energyAfter} / ${returnReport.maxEnergy} ready</p></div></div>`
      : '',
    returnReport.marketRolled
      ? '<div class="return-ledger-entry market"><span>◆</span><div><small>MARKET BELL</small><strong>New trading day</strong><p>Listings and commissions refreshed</p></div></div>'
      : '',
    returnReport.weeklyRolled
      ? '<div class="return-ledger-entry guild"><span>♜</span><div><small>GUILD BELL</small><strong>New weekly contract</strong><p>Copper & Clover posted fresh work</p></div></div>'
      : '',
  ].filter(Boolean).join('');
  return `<div class="modal-backdrop return-report-backdrop" data-action="close-modal"><div class="modal return-report-modal" data-modal-panel>
    <div class="return-glow"></div>
    <div class="modal-head"><div><p class="eyebrow">KEEPER’S RETURN LEDGER</p><h2>While the lanterns dimmed…</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
    <div class="return-hero">
      <div class="return-seal"><i></i><span>◇</span></div>
      <div><span>STALL XVII KEPT WATCH</span><strong>${awayDuration(returnReport.awayMs)} away</strong><p>The bazaar moved in your absence. Your ledger is balanced and every reward is waiting where it belongs.</p></div>
    </div>
    ${returnReport.expeditionReady && dungeon ? `<div class="return-expedition ${dungeon.tone}">
      <div class="return-depth-mark">${dungeon.icon}</div>
      <div><small>EXPEDITION AWAITING REPORT</small><h3>${dungeon.name}</h3><p>Your crew is back at the gate. Open the dungeon board to appraise their return.</p></div>
      <span>READY</span>
    </div>` : ''}
    <div class="return-ledger">${ledgerEntries}</div>
    <div class="return-actions">
      <button class="button secondary" data-action="return-stall">Open my stall</button>
      <button class="button gold-button" data-action="${returnReport.expeditionReady ? 'return-expedition' : 'return-continue'}" data-autofocus>${returnReport.expeditionReady ? 'Meet the returning crew' : 'Continue to the bazaar'} <span>→</span></button>
    </div>
  </div></div>`;
}

function expeditionResultModal() {
  if (!expeditionResult) return '';
  const dungeon = DUNGEONS.find(entry => entry.id === expeditionResult.dungeonId);
  const item = expeditionResult.itemId ? ITEMS[expeditionResult.itemId] : null;
  const party = expeditionResult.partyIds.map(id => {
    const roster = state.adventurers.find(entry => entry.id === id);
    return { ...ADVENTURERS[id], level: roster?.level || 1 };
  });
  const success = expeditionResult.success;
  return `<div class="modal-backdrop expedition-result-backdrop ${success ? 'success' : 'withdrawn'}" data-action="close-modal"><div class="modal expedition-result-modal ${success ? 'success' : 'withdrawn'}" data-modal-panel>
    <div class="expedition-result-scene ${dungeon.tone}">
      <div class="result-depth-rings"><i></i><i></i><i></i></div>
      <span class="result-depth-glyph">${success ? dungeon.icon : '⌁'}</span>
      <div class="result-scene-label">${success ? 'EXPEDITION COMPLETE' : 'PARTY RETURNED SAFELY'}</div>
    </div>
    <button class="icon-btn result-close" data-action="close-modal">×</button>
    <div class="expedition-result-body">
      <div class="expedition-result-heading"><p class="eyebrow">${dungeon.name.toUpperCase()} · ${Math.round(expeditionResult.successChance * 100)}% FORECAST</p><h2>${success ? 'The depths gave something back.' : 'Wisdom is a kind of treasure.'}</h2><p>${success ? 'Your crew crossed the gate with a marketable find and a heavier purse.' : 'The route turned against them, but every adventurer returned with gold and hard-earned experience.'}</p></div>
      ${success ? `<div class="haul-reveal ${item.tone}">
        <div class="haul-item-art"><span>${item.icon}</span><i></i></div>
        <div><p>${item.rarity} ${item.category}</p><h3>${expeditionResult.quantity}× ${item.name}</h3><span>${item.lore}</span>${expeditionResult.newDiscovery ? '<b>✦ NEW CODEX DISCOVERY</b>' : '<b class="known">Added to your collection</b>'}</div>
      </div>` : `<div class="haul-reveal empty-haul"><div class="haul-item-art"><span>○</span></div><div><p>SAFE WITHDRAWAL</p><h3>No marketable find</h3><span>The crew preserved part of the purse and every adventurer’s progress.</span><b class="known">No inventory was lost</b></div></div>`}
      <div class="expedition-ledger">
        <div><span>GOLD RECOVERED</span><strong>${money(expeditionResult.gold)} <i>◆</i></strong></div>
        <div><span>MARKET STANDING</span><strong>+${money(expeditionResult.standing)} <i>♛</i></strong></div>
        <div><span>MARKET XP</span><strong>+${expeditionResult.marketXp} <i>XP</i></strong></div>
        <div><span>CREW EXPERIENCE</span><strong>+${expeditionResult.adventurerXp} <i>each</i></strong></div>
      </div>
      <div class="result-party"><span>RETURNING CREW</span><div>${party.map(member => `<i class="${member.tone}">${member.icon}<small>Lv.${member.level}</small></i>`).join('')}</div>${expeditionResult.levelGained ? `<b>Market level ${state.level} reached</b>` : '<b>All crew accounted for</b>'}</div>
      <div class="expedition-result-actions"><button class="button secondary" data-action="result-dungeons">Return to dungeon board</button><button class="button ${success ? 'gold-button' : 'primary'}" data-action="result-stall" data-autofocus>${success ? 'Appraise at the stall' : 'Recover at the stall'} <span>→</span></button></div>
    </div>
  </div></div>`;
}

function onboardingModal() {
  const steps = [
    {
      kicker: 'STALL XVII · THE NIGHT BAZAAR',
      title: 'The doors are yours.',
      body: `<div class="onboarding-welcome">
        <div class="onboarding-seal"><i></i><span>◆</span><b>XVII</b></div>
        <p>You inherited a market stall above an endless dungeon. Send adventurers below, appraise what returns, and turn forgotten treasure into a mercantile legend.</p>
        <div class="onboarding-pill-row"><span>EXPLORE</span><i>→</i><span>COLLECT</span><i>→</i><span>TRADE</span></div>
      </div>`,
      next: 'Show me the ledger',
    },
    {
      kicker: 'THE KEEPER’S LOOP',
      title: 'Descend. Discover. Deal.',
      body: `<div class="onboarding-loop">
        <article><b>01</b><span class="green">♧</span><div><h3>Choose a depth</h3><p>Build a balanced crew. Power improves survival; luck uncovers rarer stock.</p></div></article>
        <article><b>02</b><span class="violet">◇</span><div><h3>Appraise the haul</h3><p>Every return adds real items to your cabinet, from mooncaps to legendary crowns.</p></div></article>
        <article><b>03</b><span class="orange">◆</span><div><h3>Work the counter</h3><p>Accept offers, haggle for more, or list valuables in the shared bazaar.</p></div></article>
      </div>`,
      next: 'Learn the rhythms',
    },
    {
      kicker: 'THE MARKET RHYTHMS',
      title: 'There is always another bell.',
      body: `<div class="onboarding-systems">
        <article><span>ϟ</span><div><h3>Energy returns</h3><p>One point every three minutes, even while you are away.</p></div></article>
        <article><span>▦</span><div><h3>Daily ledger</h3><p>Visit each day to build a seven-day reward streak.</p></div></article>
        <article><span>♕</span><div><h3>The Reliquary</h3><p>Earn gems through play and reveal tradeable relics with a clear guarantee.</p></div></article>
        <article><span>◈</span><div><h3>Shared bazaar</h3><p>Sign in when online play is configured to trade and climb global standings.</p></div></article>
      </div>`,
      next: 'Ready my stall',
    },
    {
      kicker: 'YOUR FIRST NIGHT',
      title: 'The gate is waiting.',
      body: `<div class="onboarding-ready">
        <div class="ready-party">${state.selectedParty.map(id => `<span class="${ADVENTURERS[id].tone}">${ADVENTURERS[id].icon}</span>`).join('')}</div>
        <p>Your first crew is assembled. Mossroot Grotto is the safest opening run, and your existing stock means the stall can serve customers immediately.</p>
        <div class="ready-ledger"><div><span>STARTING ENERGY</span><strong>${state.energy} ϟ</strong></div><div><span>STARTING PURSE</span><strong>${money(state.gold)} ◆</strong></div><div><span>CREW</span><strong>${state.selectedParty.length} ready</strong></div></div>
      </div>`,
      next: 'Open the dungeon board',
    },
  ];
  const step = steps[onboardingStep] || steps[0];
  return `<div class="modal-backdrop onboarding-backdrop"><div class="modal onboarding-modal" role="dialog" aria-modal="true" aria-labelledby="onboarding-title" data-modal-panel>
    ${storageNotice()}
    <div class="onboarding-progress" aria-label="Introduction progress">${steps.map((_, index) => `<i class="${index <= onboardingStep ? 'active' : ''}" ${index === onboardingStep ? 'aria-current="step"' : ''}></i>`).join('')}</div>
    <div class="onboarding-copy"><p class="eyebrow">${step.kicker}</p><h2 id="onboarding-title">${step.title}</h2></div>
    ${step.body}
    <div class="onboarding-actions">
      ${onboardingStep > 0 ? '<button class="text-button" data-action="onboarding-back">← Back</button>' : '<button class="text-button" data-action="onboarding-skip">Skip introduction</button>'}
      <span>${onboardingStep + 1} / ${steps.length}</span>
      <button class="button ${onboardingStep === steps.length - 1 ? 'gold-button' : 'primary'}" data-action="${onboardingStep === steps.length - 1 ? 'onboarding-finish' : 'onboarding-next'}" data-autofocus>${step.next} <b>→</b></button>
    </div>
  </div></div>`;
}

function accountModal() {
  if (!auth.configured) {
    return `<div class="modal-backdrop" data-action="close-modal"><div class="modal account-modal" data-modal-panel>
      <div class="modal-head"><div><p class="eyebrow">MERCHANT ACCOUNT</p><h2>Playing as a guest</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
      <div class="guest-account-art"><span>M</span><i>◇</i></div>
      <h3>Your local ledger is safe.</h3><p>Dungeon Market is fully playable offline. Configure Melodia’s Supabase environment to enable shared accounts, global standings, and cross-player trades.</p>
      ${saveTools()}
      <div class="account-foot"><button class="text-button danger-text" data-action="reset-save">Reset local progress</button><button class="button primary" data-action="close-modal">Continue as ${playerName()}</button></div>
    </div></div>`;
  }
  const sessionStorageWarning = auth.persistenceError
    ? auth.signedIn
      ? `<p class="account-storage-warning" role="status"><strong>Current-tab account session.</strong> Browser storage is blocked, so this login may not survive a reload. Your local game ledger is unaffected.</p>`
      : `<p class="account-storage-warning" role="status"><strong>Stored session cleanup was blocked.</strong> You are signed out in this tab, but the browser may restore the previous login after a reload until site storage can be cleared.</p>`
    : '';
  if (auth.signedIn) {
    const email = escapeText(auth.session?.user?.email || 'Shared Melodia account');
    const signingOut = accountPending === 'signout';
    return `<div class="modal-backdrop" data-action="close-modal"><div class="modal account-modal" data-modal-panel>
      <div class="modal-head"><div><p class="eyebrow">MERCHANT ACCOUNT</p><h2>Your keeper’s ledger</h2></div><button class="icon-btn" data-action="close-modal" ${signingOut ? 'disabled' : ''}>×</button></div>
      <div class="signed-account-card"><div class="account-avatar">${playerName().charAt(0).toUpperCase()}</div><div><span class="cloud-badge live"><i></i>cloud account</span><h3>${playerName()}</h3><p>${email}</p></div></div>
      <div class="account-benefits"><div><span>◈</span><strong>Shared bazaar<small>Buy and list with other players</small></strong></div><div><span>♛</span><strong>Global standing<small>Your merchant identity travels with you</small></strong></div><div><span>◇</span><strong>Melodia session<small>Also recognized by Survivor and Total Defence</small></strong></div></div>
      ${sessionStorageWarning}
      ${saveTools()}
      <div class="account-foot"><button class="text-button danger-text" data-action="reset-save" ${signingOut ? 'disabled' : ''}>Reset local progress</button><button class="button secondary" data-action="account-signout" ${signingOut ? 'disabled aria-busy="true"' : ''}>${signingOut ? 'Signing out…' : 'Sign out'}</button><button class="button primary" data-action="close-modal" ${signingOut ? 'disabled' : ''}>Return to market</button></div>
    </div></div>`;
  }
  const signingIn = accountPending === 'signin';
  const signingUp = accountPending === 'signup';
  const accountBusy = Boolean(accountPending);
  return `<div class="modal-backdrop" data-action="close-modal"><div class="modal account-modal" data-modal-panel>
    <div class="modal-head"><div><p class="eyebrow">MERCHANT ACCOUNT</p><h2>${accountMode === 'signin' ? 'Welcome back.' : 'Open a shared ledger.'}</h2></div><button class="icon-btn" data-action="close-modal" ${accountBusy ? 'disabled' : ''}>×</button></div>
    <div class="account-tabs"><button class="${accountMode === 'signin' ? 'active' : ''}" data-action="account-mode" data-mode="signin" ${accountBusy ? 'disabled' : ''}>Sign in</button><button class="${accountMode === 'signup' ? 'active' : ''}" data-action="account-mode" data-mode="signup" ${accountBusy ? 'disabled' : ''}>Create account</button></div>
    ${accountMode === 'signin' ? `<div class="account-form" ${signingIn ? 'aria-busy="true"' : ''}>
      <label><span>Username or email</span><input id="account-identifier" data-account-field="identifier" type="text" autocomplete="username" maxlength="254" value="${escapeText(accountDraft.identifier)}" placeholder="Marrowmancer" ${accountBusy ? 'disabled' : ''}></label>
      <label><span>Password</span><input id="account-password" data-account-field="signInPassword" type="password" autocomplete="current-password" maxlength="128" value="${escapeText(accountDraft.signInPassword)}" placeholder="••••••••" ${accountBusy ? 'disabled' : ''}></label>
      <button class="button primary full" data-action="account-signin" ${accountBusy ? `disabled${signingIn ? ' aria-busy="true"' : ''}` : ''}>${signingIn ? 'Opening ledger…' : 'Enter the shared bazaar <span>→</span>'}</button>
    </div>` : `<div class="account-form" ${signingUp ? 'aria-busy="true"' : ''}>
      <label><span>Merchant name</span><input id="account-username" data-account-field="username" type="text" autocomplete="username" maxlength="20" value="${escapeText(accountDraft.username)}" placeholder="Marrowmancer" ${accountBusy ? 'disabled' : ''}></label>
      <label><span>Email</span><input id="account-email" data-account-field="email" type="email" autocomplete="email" maxlength="254" value="${escapeText(accountDraft.email)}" placeholder="keeper@example.com" ${accountBusy ? 'disabled' : ''}></label>
      <label><span>Password</span><input id="account-password" data-account-field="signUpPassword" type="password" autocomplete="new-password" minlength="6" maxlength="128" value="${escapeText(accountDraft.signUpPassword)}" placeholder="At least 6 characters" ${accountBusy ? 'disabled' : ''}></label>
      <button class="button gold-button full" data-action="account-signup" ${accountBusy ? `disabled${signingUp ? ' aria-busy="true"' : ''}` : ''}>${signingUp ? 'Creating account…' : 'Create merchant account <span>→</span>'}</button>
    </div>`}
    ${sessionStorageWarning}
    ${saveTools()}
    <p class="account-privacy">Your account is shared across Melodia games. Guest progress remains stored locally on this device.</p>
  </div></div>`;
}

function saveTools() {
  return `<section class="save-tools" aria-labelledby="save-tools-title">
    <div><span id="save-tools-title">LOCAL SAVE TOOLS</span><p>Keep a portable copy of this device’s progress. Account credentials and cloud-sync records are never included.</p></div>
    <div class="save-tool-actions">
      <button class="button secondary" data-action="download-save-backup"><span aria-hidden="true">↓</span> Download backup</button>
      <button class="button secondary" data-action="choose-save-backup"><span aria-hidden="true">↑</span> Restore backup</button>
      <input id="save-restore-file" type="file" accept="application/json,.json" data-save-restore-file hidden>
    </div>
  </section>`;
}

function restoreSaveModal() {
  if (!pendingRestore) return '';
  const restored = pendingRestore.state;
  const itemCount = restored.inventory.reduce((total, entry) => total + entry.quantity, 0);
  const exported = new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(pendingRestore.exportedAt);
  return `<div class="modal-backdrop restore-backdrop" data-action="close-modal"><div class="modal restore-modal" data-modal-panel>
    <div class="modal-head"><div><p class="eyebrow">PORTABLE KEEPER’S LEDGER</p><h2>Restore this backup?</h2></div><button class="icon-btn" data-action="close-modal">×</button></div>
    <div class="restore-mark"><span>⇄</span></div>
    <p class="restore-intro">This will replace the progress stored in this browser. Your current ledger remains untouched until you confirm.</p>
    <div class="restore-summary" aria-label="Backup summary">
      <div><span>EXPORTED</span><strong>${escapeText(exported)}</strong></div>
      <div><span>MARKET LEVEL</span><strong>${restored.level}</strong></div>
      <div><span>PURSE</span><strong>${money(restored.gold)} ◆</strong></div>
      <div><span>GEMS</span><strong>${restored.gems} ✦</strong></div>
      <div><span>COLLECTION</span><strong>${money(itemCount)} items</strong></div>
      <div><span>DISCOVERIES</span><strong>${restored.discoveries.length} / ${Object.keys(ITEMS).length}</strong></div>
    </div>
    <p class="restore-note">${auth.signedIn ? 'After restoration, the shared trade ledger will ask which balance should lead. Cloud sale proceeds are never silently overwritten.' : 'Only local progress is restored. Sign in afterward if you want to reconcile with a shared trade ledger.'}</p>
    <div class="confirm-actions"><button class="button secondary" data-action="close-modal">Keep current ledger</button><button class="button gold-button" data-action="confirm-save-restore" data-autofocus>Restore backup</button></div>
  </div></div>`;
}

function summonResultCard(reward, index) {
  const item = ITEMS[reward.itemId];
  const banner = reward.newDiscovery || reward.guaranteed
    ? `<b class="result-discovery ${reward.guaranteed ? 'guaranteed' : ''}">${reward.newDiscovery ? 'NEW CODEX' : ''}${reward.newDiscovery && reward.guaranteed ? ' · ' : ''}${reward.guaranteed ? 'FATE GUARANTEE' : ''}</b>`
    : '';
  return `<article class="summon-result-card ${reward.rarity.toLowerCase()} ${reward.newDiscovery ? 'new-discovery' : ''}" style="--reveal-index:${index}"><div class="result-item-art">${item.icon}<span></span>${banner}</div><p>${reward.rarity}</p><h3>${item.name}</h3><small>${item.category} · ${reward.ownedAfter} owned</small></article>`;
}

function summonHistory() {
  const stats = state.summonStats || { total: 0, Legendary: 0, Epic: 0, Rare: 0 };
  if (!state.summonHistory.length) return `<div class="summon-history empty"><span>◇</span>No previous draws in this ledger.</div>`;
  return `<div class="summon-history"><span>RELIQUARY LEDGER · ${money(stats.total)} DRAWS · ${money(stats.Legendary)} LEGENDARY</span><div>${state.summonHistory.slice(0, 8).map(entry => {
    const item = ITEMS[entry.itemId];
    return `<i class="${entry.rarity.toLowerCase()}" title="${item.name} · ${entry.rarity}">${item.icon}</i>`;
  }).join('')}</div></div>`;
}

function availableListings() {
  return MARKET_LISTINGS.filter(listing => !state.purchasedListings.includes(listing.id));
}

function soldOutMessage(shared = false) {
  if (shared) return `<div class="sold-out"><span>◇</span><div><strong>The shared board is quiet.</strong><p>New player listings appear here as merchants publish them.</p></div></div>`;
  return `<div class="sold-out"><span>✓</span><div><strong>You cleared today’s board.</strong><p>Fresh listings arrive in <b data-market-clock>${marketBellCaption()}</b>.</p></div></div>`;
}

function inventoryCount() {
  return state.inventory.reduce((total, entry) => total + entry.quantity, 0);
}

function dailyReadyCount() {
  const ready = DAILY_COMMISSIONS.filter(commission => (
    (state.dailyCommissions.progress[commission.metric] || 0) >= commission.target
    && !state.dailyCommissions.claimed.includes(commission.id)
  )).length;
  const bonusReady = DAILY_COMMISSIONS.every(commission => state.dailyCommissions.claimed.includes(commission.id))
    && !state.dailyCommissions.bonusClaimed;
  return ready + Number(bonusReady);
}

function achievementReadyCount() {
  return ACHIEVEMENTS.filter(achievement => (
    state.achievements.unlocked.includes(achievement.id)
    && !state.achievements.claimed.includes(achievement.id)
  )).length;
}

function guildReadyCount() {
  return dailyReadyCount() + achievementReadyCount() + campaignReadyCount();
}

function campaignReadyCount() {
  if (!cloud.signedIn || !social.campaign || social.campaignError) return 0;
  const total = Math.max(0, Number(social.campaign.total_points) || 0);
  const claimed = new Set((Array.isArray(social.campaign.claimed_tiers) ? social.campaign.claimed_tiers : []).map(Number));
  return CAMPAIGN_TIERS.filter(tier => total >= tier.target && !claimed.has(tier.tier)).length;
}

function marketPathReadyCount() {
  return MARKET_PATH_REWARDS.filter(reward => (
    reward.level <= state.level
    && !state.marketPath.claimed.includes(reward.level)
  )).length;
}

function availableLanterns() {
  if (!cloud.signedIn || social.mode !== 'live' || social.rosterError) return 0;
  return social.roster.filter(merchant => !merchant.is_you && !merchant.lantern_sent_today).length;
}

function ledgerRecency(value, now = Date.now()) {
  const updated = new Date(value).getTime();
  if (!Number.isFinite(updated)) return 'Ledger recently opened';
  const minutes = Math.max(0, Math.floor((now - updated) / 60000));
  if (minutes < 2) return 'Ledger updated just now';
  if (minutes < 60) return `Ledger updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Ledger updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Ledger updated ${days}d ago`;
}

function tradeTimestamp(value) {
  const timestamp = new Date(value);
  if (!Number.isFinite(timestamp.getTime())) return 'Recently completed';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(timestamp);
}

function discoveredCount() {
  return state.discoveries.length;
}

function relicCount() {
  return state.inventory.filter(entry => ITEMS[entry.itemId]?.category === 'Relic').reduce((total, entry) => total + entry.quantity, 0);
}

function standingLabel() {
  const rank = rankedLeaders().findIndex(leader => leader.isYou || leader.name === 'YOU') + 1;
  if (rank <= 0) return 'Unranked';
  return `${social.mode === 'live' ? 'Verified' : 'Local'} weekly rank #${String(rank).padStart(2, '0')}`;
}

function energyCaption() {
  const forecast = energyRecoveryForecast(state);
  return forecast.missing ? `next point in ${duration(forecast.nextIn)}` : 'fully restored';
}

function energyFullCaption() {
  const forecast = energyRecoveryForecast(state);
  return forecast.missing
    ? `${forecast.missing} missing · full in ${duration(forecast.fullIn)}`
    : 'Capacity ready for the next expedition';
}

function expeditionAlertPermission() {
  if (typeof globalThis.Notification !== 'function') return 'unsupported';
  return Notification.permission;
}

async function showExpeditionNotification(dungeon) {
  const targetUrl = new URL('./?tab=dungeons', document.baseURI).href;
  const options = {
    body: `${dungeon.name} is complete. Your crew and haul are waiting at the gate.`,
    icon: new URL('./dungeon-market-icon-192.png', document.baseURI).href,
    badge: new URL('./dungeon-market-icon-192.png', document.baseURI).href,
    tag: `dungeon-market-expedition-${dungeon.id}`,
    renotify: false,
    data: { url: targetUrl },
  };
  try {
    const registration = await navigator.serviceWorker?.getRegistration?.();
    if (registration?.showNotification) {
      await registration.showNotification('Your expedition has returned', options);
      return;
    }
    const notification = new Notification('Your expedition has returned', options);
    notification.onclick = () => {
      window.focus();
      window.location.assign(targetUrl);
      notification.close();
    };
  } catch {
    // A browser or OS may revoke notification capability after permission was
    // granted. The ready state still appears in-game on the next visit.
  }
}

function dispatchExpeditionAlert(now = Date.now()) {
  const marked = markExpeditionAlerted(state, now);
  if (!marked.ok) return false;
  const dungeon = DUNGEONS.find(entry => entry.id === state.expedition.dungeonId);
  state = marked.state;
  persist({ cloud: false });
  if (document.visibilityState === 'visible') {
    showToast(`${dungeon.name} is complete. Your haul is waiting at the gate.`);
  } else {
    void showExpeditionNotification(dungeon);
  }
  return true;
}

function nextMarketBell(now = Date.now()) {
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime();
}

function marketBellCaption(now = Date.now()) {
  const seconds = Math.max(0, Math.ceil((nextMarketBell(now) - now) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

function nextGuildBell(now = Date.now()) {
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  const daysUntilMonday = ((8 - monday.getDay()) % 7) || 7;
  monday.setDate(monday.getDate() + daysUntilMonday);
  return monday.getTime();
}

function weeklyContractCaption(now = Date.now()) {
  const seconds = Math.max(0, Math.ceil((nextGuildBell(now) - now) / 1000));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return `${days}d ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

function verifiedBoardCaption(now = Date.now()) {
  const seconds = Math.max(0, Math.ceil((nextUtcWeekBoundary(now) - now) / 1000));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return `${days}d ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

function scheduleRuntimeTick() {
  clearTimeout(timer);
  timer = null;
  if (externalSaveChange) return;
  const now = Date.now();
  const needsSecondTick = state.energy < state.maxEnergy
    || state.expedition
    || state.activeTab === 'market'
    || state.activeTab === 'guild'
    || state.activeTab === 'leaderboard';
  const delay = runtimeTickDelay({
    now,
    marketBoundary: nextMarketBell(now),
    guildBoundary: nextGuildBell(now),
    backgroundWakeAt: state.expedition?.notifyOnComplete
      && !state.expedition.notifiedAt
      && expeditionAlertPermission() === 'granted'
      ? state.expedition.endsAt
      : null,
    needsSecondTick,
    visible: document.visibilityState === 'visible',
  });
  if (delay !== null) timer = setTimeout(runtimeTick, delay);
}

function runtimeTick() {
  const now = Date.now();
  let synchronized = rollDailyState(state, now);
  synchronized = rollWeeklyState(synchronized, now);
  synchronized = regenerateEnergy(synchronized, now);
  if (synchronized !== state) {
    state = synchronized;
    persist();
    render();
    return;
  }
  if (dispatchExpeditionAlert(now)) return;

  document.querySelectorAll('[data-energy-value]').forEach(element => {
    element.textContent = `${state.energy} / ${state.maxEnergy}`;
  });
  document.querySelectorAll('[data-energy-caption]').forEach(element => {
    element.textContent = energyCaption();
  });
  document.querySelectorAll('[data-energy-bar]').forEach(element => {
    element.style.width = `${Math.round(state.energy / state.maxEnergy * 100)}%`;
  });
  document.querySelectorAll('[data-energy-forecast]').forEach(element => {
    element.textContent = energyFullCaption();
  });
  document.querySelectorAll('[data-market-clock]').forEach(element => {
    element.textContent = marketBellCaption(now);
  });
  document.querySelectorAll('[data-weekly-clock]').forEach(element => {
    element.textContent = weeklyContractCaption(now);
  });
  document.querySelectorAll('[data-ranking-clock]').forEach(element => {
    element.textContent = social.mode === 'live'
      ? verifiedBoardCaption(now)
      : weeklyContractCaption(now);
  });

  if (state.expedition) {
    const remaining = Math.max(0, state.expedition.endsAt - now);
    if (remaining <= 0) {
      render();
      return;
    }
    const total = state.expedition.endsAt - state.expedition.startedAt;
    const progress = Math.min(100, Math.max(0, 100 - (remaining / total) * 100));
    document.querySelectorAll('[data-run-time]').forEach(element => {
      element.textContent = `${duration(remaining)} remaining`;
    });
    document.querySelectorAll('[data-run-progress]').forEach(element => {
      element.textContent = `${Math.round(progress)}%`;
    });
    document.querySelectorAll('[data-run-bar]').forEach(element => {
      element.style.width = `${progress}%`;
    });
  }
  scheduleRuntimeTick();
}

function playerName() {
  return escapeText(auth.username || 'Marrowmancer');
}

function titleCase(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function showToast(message) {
  toast = message;
  render();
  setTimeout(() => {
    if (toast === message) {
      toast = null;
      const element = document.querySelector('.toast');
      if (element) {
        element.textContent = '';
        element.classList.remove('show');
      }
    }
  }, 2600);
}

function progressionMessage(message, previousLevel) {
  return state.level > previousLevel
    ? `Market level ${state.level} reached! ${message}`
    : message;
}

function prepareModal() {
  const panel = document.querySelector('[data-modal-panel]');
  if (!panel) return;
  const shell = document.querySelector('.app-shell');
  if (shell) {
    shell.inert = true;
    shell.setAttribute('aria-hidden', 'true');
  }
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('tabindex', '-1');
  const heading = panel.querySelector('h2');
  if (heading) {
    heading.id ||= 'active-modal-title';
    panel.setAttribute('aria-labelledby', heading.id);
  } else {
    panel.setAttribute('aria-label', 'Dungeon Market dialog');
  }
  panel.querySelectorAll('[data-action="close-modal"]').forEach(button => {
    button.setAttribute('aria-label', 'Close dialog');
  });
  const initialFocus = panel.querySelector('[data-autofocus]')
    || panel.querySelector('input:not(:disabled)')
    || panel.querySelector('button:not([data-action="close-modal"]):not(:disabled)')
    || panel.querySelector('[data-action="close-modal"]')
    || panel;
  initialFocus.focus({ preventScroll: true });
}

function rememberFocus(element) {
  if (!element || modal) return;
  focusReturn = {};
  for (const key of ['action', 'tab', 'item', 'dungeon', 'listing']) {
    if (element.dataset[key]) focusReturn[key] = element.dataset[key];
  }
}

function requestOnlineAccount(element, purpose) {
  rememberFocus(element);
  accountMode = 'signin';
  modal = 'account';
  showToast(`Sign in here to ${purpose}. Your local ledger will stay intact.`);
}

function downloadSaveRecovery() {
  if (!storageHealth.recoveryRaw) return false;
  const blob = new Blob([storageHealth.recoveryRaw], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dungeon-market-recovery-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}

function downloadSaveBackup() {
  const raw = serializeGameBackup(state);
  const blob = new Blob([raw], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dungeon-market-backup-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function readSaveBackup(file) {
  if (!file) return;
  if (file.size > MAX_BACKUP_BYTES) {
    showToast('That backup is too large to restore safely.');
    return;
  }
  let raw;
  try {
    raw = await file.text();
  } catch {
    showToast('The browser could not read that backup file.');
    return;
  }
  const parsed = parseGameBackup(raw);
  if (!parsed.ok) {
    showToast(parsed.error);
    return;
  }
  pendingRestore = {
    exportedAt: parsed.exportedAt,
    state: hydrateState(parsed.game, Date.now()),
  };
  modal = 'restore-save';
  render();
}

function restoreFocus() {
  const remembered = focusReturn;
  focusReturn = null;
  if (!remembered || !Object.keys(remembered).length) return;
  const candidates = [...document.querySelectorAll('button[data-action], button[data-tab]')]
    .filter(element => element.getClientRects().length > 0 && !element.disabled);
  const target = candidates.find(element => Object.entries(remembered)
    .every(([key, value]) => element.dataset[key] === value));
  target?.focus({ preventScroll: true });
}

function closeActiveModal() {
  if (!modal || modal === 'onboarding' || modal === 'cloud-conflict') return false;
  if (modal === 'account' && accountPending) return false;
  if (modal === 'expedition-result') expeditionResult = null;
  if (modal === 'return-report') returnReport = null;
  if (modal === 'listing') {
    listingItemId = null;
    listingDraftPrice = null;
    listingDraftQuantity = 1;
  }
  if (modal === 'account') clearAccountDraft();
  if (modal === 'restore-save') pendingRestore = null;
  modal = null;
  render();
  restoreFocus();
  return true;
}

function focusableElements(panel) {
  return [...panel.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])')]
    .filter(element => element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');
}

function handleDialogKeydown(event) {
  const panel = document.querySelector('[data-modal-panel]');
  if (!panel) return;
  if (event.key === 'Escape') {
    if (closeActiveModal()) event.preventDefault();
    return;
  }
  if (modal === 'onboarding' && event.key === 'ArrowRight') {
    event.preventDefault();
    panel.querySelector('[data-action="onboarding-next"], [data-action="onboarding-finish"]')?.click();
    return;
  }
  if (modal === 'onboarding' && event.key === 'ArrowLeft' && onboardingStep > 0) {
    event.preventDefault();
    panel.querySelector('[data-action="onboarding-back"]')?.click();
    return;
  }
  if (event.key === 'Enter' && event.target.matches('input')) {
    const submitAction = modal === 'listing'
      ? 'create-online-listing'
      : modal === 'account'
        ? accountMode === 'signup' ? 'account-signup' : 'account-signin'
        : null;
    if (submitAction) {
      event.preventDefault();
      panel.querySelector(`[data-action="${submitAction}"]`)?.click();
      return;
    }
  }
  if (event.key !== 'Tab') return;
  const focusables = focusableElements(panel);
  if (!focusables.length) {
    event.preventDefault();
    panel.focus();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function bindEvents() {
  document.querySelectorAll('[data-tab]').forEach(element => element.addEventListener('click', () => {
    state = { ...state, activeTab: element.dataset.tab };
    const url = new URL(window.location.href);
    if (state.activeTab === 'overview') url.searchParams.delete('tab');
    else url.searchParams.set('tab', state.activeTab);
    history.replaceState(null, '', url);
    modal = null;
    render();
    announceViewChange();
  }));
  document.querySelectorAll('[data-filter]').forEach(element => element.addEventListener('click', () => {
    collectionFilter = element.dataset.filter;
    render();
  }));
  document.querySelectorAll('[data-market-sort]').forEach(element => element.addEventListener('change', () => {
    marketSort = element.value;
    render();
  }));
  document.querySelectorAll('[data-market-category]').forEach(element => element.addEventListener('change', () => {
    marketCategory = element.value;
    render();
  }));
  document.querySelectorAll('#listing-quantity, #listing-price').forEach(element => element.addEventListener('input', updateListingPreview));
  document.querySelectorAll('[data-save-restore-file]').forEach(element => element.addEventListener('change', async () => {
    const [file] = element.files || [];
    element.value = '';
    await readSaveBackup(file);
  }));
  document.querySelectorAll('[data-account-field]').forEach(element => element.addEventListener('input', () => {
    const field = element.dataset.accountField;
    if (Object.prototype.hasOwnProperty.call(accountDraft, field)) {
      accountDraft[field] = element.value;
    }
  }));
  document.querySelectorAll('[data-action]').forEach(element => element.addEventListener('click', event => {
    if (element.closest('[data-modal-panel]') && element.dataset.action === 'close-modal') event.stopPropagation();
    handleAction(element.dataset.action, element);
  }));
  document.querySelectorAll('[data-modal-panel]').forEach(element => element.addEventListener('click', event => event.stopPropagation()));
}

function announceViewChange() {
  const heading = document.querySelector('.content h1');
  const target = heading || document.querySelector('#main-content');
  if (!target) return;
  target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  if (heading?.textContent) document.title = `${heading.textContent.trim()} — Dungeon Market`;
}

function updateListingPreview() {
  if (modal !== 'listing' || !listingItemId) return;
  const quantityInput = document.querySelector('#listing-quantity');
  const priceInput = document.querySelector('#listing-price');
  const button = document.querySelector('[data-action="create-online-listing"]');
  const owned = state.inventory.find(entry => entry.itemId === listingItemId)?.quantity || 0;
  const quantity = Number(quantityInput?.value);
  const price = Number(priceInput?.value);
  const item = ITEMS[listingItemId];
  listingDraftQuantity = quantity;
  listingDraftPrice = price;
  const economics = listingEconomics(quantity, price, owned, item?.value);
  const valid = economics.valid;
  quantityInput?.closest('.price-field')?.classList.toggle('invalid', !Number.isInteger(quantity) || quantity < 1 || quantity > Math.min(999, owned));
  priceInput?.closest('.price-field')?.classList.toggle('invalid', !Number.isInteger(price) || price < economics.minPrice || price > economics.maxPrice);
  if (priceInput && economics.minPrice && economics.maxPrice) {
    priceInput.min = String(economics.minPrice);
    priceInput.max = String(economics.maxPrice);
    const hint = priceInput.closest('.price-field')?.querySelector('small');
    if (hint) hint.textContent = `Safe range: ${money(economics.minPrice)}–${money(economics.maxPrice)} ◆.`;
  }
  if (button) {
    button.disabled = !valid;
    button.innerHTML = valid ? `Publish ${quantity}× bundle <span>→</span>` : 'Check quantity and price';
  }
  if (!valid) return;
  const net = document.querySelector('[data-listing-net]');
  const unit = document.querySelector('[data-listing-unit]');
  if (net) net.textContent = `${money(economics.net)} ◆`;
  if (unit) unit.textContent = `${money(economics.unitPrice)} each · ${money(economics.fee)} fee`;
}

document.addEventListener('keydown', handleDialogKeydown);

async function handleAction(action, element) {
  if (action === 'adopt-external-save' && externalSaveChange) {
    const incoming = externalSaveChange;
    const activeTab = state.activeTab;
    externalSaveChange = null;
    clearAccountDraft();
    localSaveRevision = incoming.revision;
    state = hydrateState(incoming.value, Date.now());
    state.activeTab = activeTab;
    returnReport = null;
    cloudConflict = null;
    cloudReady = false;
    modal = state.onboardingComplete ? null : 'onboarding';
    if (cloud.signedIn) await restoreAccount();
    else render();
    showToast(incoming.value
      ? 'Loaded the newer browser ledger.'
      : 'Loaded the fresh ledger created in the other tab.');
    return;
  }
  if (action === 'keep-local-save' && externalSaveChange) {
    const interruptedModal = externalSaveChange.interruptedModal;
    localSaveRevision = Math.max(localSaveRevision, externalSaveChange.revision);
    externalSaveChange = null;
    modal = interruptedModal;
    persist();
    render();
    showToast('This tab now leads the browser ledger.');
    return;
  }
  if (externalSaveChange && action !== 'reload-update') {
    showToast('Choose which tab should lead before continuing.');
    return;
  }
  if (mutationCoordinator.ledgerActive) {
    showToast('The keeper is finishing a cloud-ledger transaction. Try that action again in a moment.');
    return;
  }
  if (action === 'download-save-backup') {
    downloadSaveBackup();
    showToast('Portable ledger downloaded.');
    return;
  }
  if (action === 'choose-save-backup') {
    document.querySelector('[data-save-restore-file]')?.click();
    return;
  }
  if (action === 'confirm-save-restore') {
    if (!pendingRestore) return;
    clearTimeout(cloudSyncTimer);
    clearTimeout(socialRefreshTimer);
    socialRefreshes.invalidate();
    cloudSyncTimer = null;
    socialRefreshTimer = null;
    state = {
      ...pendingRestore.state,
      activeTab: 'overview',
      cloudSync: undefined,
    };
    pendingRestore = null;
    returnReport = null;
    cloudConflict = null;
    cloudReady = false;
    summonResult = [];
    expeditionResult = null;
    collectionFilter = 'All';
    modal = null;
    persist({ cloud: false });
    if (cloud.signedIn) await restoreAccount();
    else render();
    showToast('Backup restored. Your portable ledger is now active.');
    return;
  }
  if (action === 'reload-update') {
    persist({ cloud: false });
    window.location.reload();
    return;
  }
  if (action === 'retry-storage') {
    if (storageHealth.issue === 'read') {
      const result = readGameSave(browserStorage, STORAGE_KEY);
      if (result.status === 'unavailable') {
        showToast('Browser storage is still unavailable.');
        return;
      }
      if (result.status === 'ok') {
        const activeTab = state.activeTab;
        state = hydrateState(result.value, Date.now());
        state.activeTab = activeTab;
        storageHealth = { issue: null, recoveryRaw: null, error: null };
        render();
        showToast('Your browser ledger was restored.');
        return;
      }
      if (result.status === 'corrupt') {
        storageHealth = {
          issue: 'corrupt',
          recoveryRaw: result.recoveryRaw,
          error: null,
        };
      } else {
        storageHealth = { issue: null, recoveryRaw: null, error: null };
      }
    }
    persist({ cloud: false });
    showToast(storageHealth.issue === 'write'
      ? 'The browser still cannot save progress.'
      : 'Local saving is available again.');
    return;
  }
  if (action === 'download-save-recovery') {
    showToast(downloadSaveRecovery()
      ? 'Recovery copy downloaded.'
      : 'No recovery copy is available.');
    return;
  }
  if (action === 'dismiss-save-recovery') {
    storageHealth = { issue: null, recoveryRaw: null, error: null };
    render();
    return;
  }
  if (action === 'open-achievements') {
    rememberFocus(element);
    modal = 'achievements';
    render();
    return;
  }
  if (action === 'claim-achievement') {
    commit(claimAchievement(state, element.dataset.achievement));
    return;
  }
  if (action === 'claim-achievements-all') {
    commit(claimAvailableAchievements(state));
    return;
  }
  if (action === 'cloud-use-device' || action === 'cloud-use-server') {
    if (!cloudConflict || cloudConflict.userId !== cloud.userId) return;
    const useServer = action === 'cloud-use-server';
    state = chooseCloudLedger(state, cloud.userId, cloudConflict.server, useServer ? 'server' : 'device');
    if (useServer) {
      state = recordDiscoveries(state, state.inventory.map(entry => entry.itemId));
      state = ensureCustomerOffer(state);
    }
    cloudConflict = null;
    modal = null;
    cloudReady = true;
    persist({ cloud: false });
    if (!useServer) scheduleCloudSync(0);
    render();
    showToast(useServer ? 'Cloud trade ledger restored on this device.' : 'This device now leads your cloud trade ledger.');
    return;
  }
  if (action === 'open-market-path') {
    rememberFocus(element);
    modal = 'market-path';
    render();
    return;
  }
  if (action === 'claim-market-path') {
    commit(claimMarketPathReward(state, element.dataset.rewardLevel));
    return;
  }
  if (action === 'claim-market-path-all') {
    commit(claimAvailableMarketPathRewards(state));
    return;
  }
  if (action === 'return-expedition' || action === 'return-stall' || action === 'return-continue') {
    state = {
      ...state,
      activeTab: action === 'return-expedition'
        ? 'dungeons'
        : action === 'return-stall'
          ? 'stall'
          : 'overview',
    };
    returnReport = null;
    modal = null;
    persist();
    render();
    return;
  }
  if (action === 'onboarding-next') {
    onboardingStep = Math.min(3, onboardingStep + 1);
    render();
    return;
  }
  if (action === 'onboarding-back') {
    onboardingStep = Math.max(0, onboardingStep - 1);
    render();
    return;
  }
  if (action === 'onboarding-skip') {
    state = { ...state, onboardingComplete: true };
    onboardingStep = 0;
    modal = null;
    persist();
    showToast('The keeper’s notes are waiting in your journal.');
    return;
  }
  if (action === 'onboarding-finish') {
    state = {
      ...state,
      onboardingComplete: true,
      journal: ['Opened stall seventeen and assembled the first expedition crew.', ...state.journal].slice(0, 12),
    };
    onboardingStep = 0;
    modal = 'dungeon';
    persist();
    showToast('Your first expedition is ready to depart.');
    return;
  }
  if (action === 'open-account') {
    rememberFocus(element);
    clearAccountDraft();
    accountMode = 'signin';
    modal = 'account';
    render();
    return;
  }
  if (action === 'account-mode') {
    if (accountPending) return;
    accountMode = element.dataset.mode === 'signup' ? 'signup' : 'signin';
    render();
    return;
  }
  if (action === 'account-signin') {
    const identifier = document.querySelector('#account-identifier')?.value || '';
    const password = document.querySelector('#account-password')?.value || '';
    accountDraft.identifier = identifier;
    accountDraft.signInPassword = password;
    const result = await runAccountOperation(
      'signin',
      () => auth.signIn(identifier, password),
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error);
      return;
    }
    clearAccountDraft();
    socialRefreshes.invalidate();
    modal = null;
    social = { mode: 'connecting', leaders: [], listings: [], mine: [], history: [], historyError: false, roster: [], rosterError: false, campaign: null, campaignError: false };
    showToast(result.warning
      ? `Welcome back, ${result.username}. ${result.warning}`
      : `Welcome back, ${result.username}.`);
    const bootstrap = await cloud.bootstrap();
    cloudReady = reconcileCloudBootstrap(bootstrap) && !cloudConflict;
    if (cloudReady) scheduleCloudSync(0);
    await refreshSocial();
    restoreFocus();
    return;
  }
  if (action === 'account-signup') {
    const username = document.querySelector('#account-username')?.value || '';
    const email = document.querySelector('#account-email')?.value || '';
    const password = document.querySelector('#account-password')?.value || '';
    accountDraft = {
      ...accountDraft,
      username,
      email,
      signUpPassword: password,
    };
    const result = await runAccountOperation(
      'signup',
      () => auth.signUp({ username, email, password }),
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error);
      return;
    }
    if (!result.confirmed) {
      accountDraft.identifier = email;
      accountDraft.signInPassword = '';
      accountDraft.signUpPassword = '';
      accountMode = 'signin';
      render();
      showToast('Check your email, then sign in to finish opening the ledger.');
      return;
    }
    clearAccountDraft();
    socialRefreshes.invalidate();
    modal = null;
    social = { mode: 'connecting', leaders: [], listings: [], mine: [], history: [], historyError: false, roster: [], rosterError: false, campaign: null, campaignError: false };
    showToast(result.warning
      ? `Merchant account created for ${result.username}. ${result.warning}`
      : `Merchant account created for ${result.username}.`);
    const bootstrap = await cloud.bootstrap();
    cloudReady = reconcileCloudBootstrap(bootstrap) && !cloudConflict;
    if (cloudReady) scheduleCloudSync(0);
    await refreshSocial();
    restoreFocus();
    return;
  }
  if (action === 'account-signout') {
    socialRefreshes.invalidate();
    const result = await runAccountOperation('signout', () => auth.signOut());
    if (!result) return;
    cloudReady = false;
    clearTimeout(cloudSyncTimer);
    clearAccountDraft();
    modal = null;
    social = { mode: cloud.configured ? 'connecting' : 'guest', leaders: [], listings: [], mine: [], history: [], historyError: false, roster: [], rosterError: false, campaign: null, campaignError: false };
    showToast(result.warning
      ? `Signed out for this tab. ${result.warning}`
      : 'Signed out. Your local guest ledger remains available.');
    await refreshSocial();
    restoreFocus();
    return;
  }
  if (action === 'open-dungeon') {
    rememberFocus(element);
    modal = 'dungeon';
    render();
    return;
  }
  if (action === 'open-summon') {
    rememberFocus(element);
    summonResult = [];
    modal = 'summon';
    render();
    return;
  }
  if (action === 'close-modal') {
    closeActiveModal();
    return;
  }
  if (action === 'return-summon') {
    modal = 'summon';
    render();
    return;
  }
  if (action === 'result-dungeons' || action === 'result-stall') {
    state = { ...state, activeTab: action === 'result-stall' ? 'stall' : 'dungeons' };
    expeditionResult = null;
    modal = null;
    persist();
    render();
    return;
  }
  if (action === 'claim-login') commit(claimDaily(state));
  if (action === 'claim-commission') commit(claimDailyCommission(state, element.dataset.commission));
  if (action === 'claim-commission-bonus') commit(claimDailyCommissionBonus(state));
  if (action === 'claim-weekly') commit(claimWeeklyContract(state));
  if (action === 'upgrade-renovation') commit(upgradeRenovation(state, element.dataset.renovation));
  if (action === 'accept-customer') commit(acceptCustomerOffer(state));
  if (action === 'haggle-customer') commit(haggleCustomerOffer(state));
  if (action === 'decline-customer') commit(declineCustomerOffer(state));
  if (action === 'start-expedition') commit(startExpedition(state, element.dataset.dungeon), true);
  if (action === 'toggle-expedition-alert') {
    if (!state.expedition) return;
    if (state.expedition.notifyOnComplete) {
      commit(setExpeditionAlert(state, false));
      return;
    }
    const support = expeditionAlertPermission();
    if (support === 'unsupported') {
      showToast('This browser does not support expedition alerts.');
      return;
    }
    let permission = support;
    if (permission === 'default') {
      try {
        permission = await Notification.requestPermission();
      } catch {
        permission = 'denied';
      }
    }
    if (permission !== 'granted') {
      render();
      showToast('Expedition alerts are blocked. You can allow them in your browser settings.');
      return;
    }
    commit(setExpeditionAlert(state, true));
  }
  if (action === 'complete-expedition') {
    const previous = state;
    const previousLevel = state.level;
    const result = completeExpedition(state);
    if (!result.ok) {
      commit(result);
      return;
    }
    rememberFocus(element);
    const wasDiscovered = result.reward.itemId
      ? previous.discoveries.includes(result.reward.itemId)
      : true;
    state = ensureCustomerOffer(result.state);
    expeditionResult = {
      ...result.reward,
      newDiscovery: Boolean(result.reward.itemId && !wasDiscovered),
      levelGained: state.level > previousLevel,
    };
    modal = 'expedition-result';
    persist();
    render();
    return;
  }
  if (action === 'toggle-party') commit(togglePartyMember(state, element.dataset.adventurer));
  if (action === 'buy-listing') commit(buyListing(state, element.dataset.listing));
  if (action === 'buy-online-listing') {
    if (!cloud.signedIn) {
      requestOnlineAccount(element, 'trade in the shared bazaar');
      return;
    }
    const listingId = element.dataset.listing;
    const expectedListing = social.listings.find(listing => listing.id === listingId);
    if (!expectedListing) {
      showToast('That bazaar listing is no longer on the board. Refreshing the ledger.');
      await refreshSocial();
      return;
    }
    const result = await runMutation(
      `buy-listing:${listingId}`,
      element,
      'Buying…',
      () => cloud.buyListing(listingId),
      { ledger: true },
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error || 'The listing is no longer available.');
      return;
    }
    const purchase = normalizePurchaseReceipt(
      Array.isArray(result.data) ? result.data[0] : result.data,
      KNOWN_ITEM_IDS,
    );
    if (
      !purchase
      || purchase.item_id !== expectedListing.item_id
      || purchase.quantity !== expectedListing.quantity
      || purchase.price !== expectedListing.price
    ) {
      showToast('The trade receipt changed, so the cloud ledger is being reconciled before local stock is updated.');
      await refreshSocial();
      return;
    }
    const scoreReceipt = verifiedTradeAward(purchase, state.marketScore);
    if (!scoreReceipt) {
      showToast('Trade completed, but the receipt was incomplete.');
      await refreshSocial();
      return;
    }
    const purchaseQuantity = purchase.quantity;
    const purchaseGold = purchase.gold;
    const purchaseMarketScore = purchase.market_score;
    const verifiedScore = scoreReceipt.points;
    const journalStanding = scoreReceipt.exact
      ? `+${verifiedScore} verified standing`
      : 'standing reconciled from the bazaar';
    const previousLevel = state.level;
    state = recordDailyProgress(recordDiscoveries(awardMarketXp({
      ...state,
      gold: purchaseGold,
      marketScore: purchaseMarketScore,
      inventory: addInventory(state.inventory, purchase.item_id, purchaseQuantity),
      journal: [
        `Online bazaar: bought ${purchaseQuantity}× ${ITEMS[purchase.item_id].name}; ${journalStanding}.`,
        ...state.journal,
      ].slice(0, 12),
    }, verifiedTradeXp(verifiedScore)), purchase.item_id), 'trades');
    state = ensureCustomerOffer(state);
    rebaseCloudLedger(
      { inventory: { [purchase.item_id]: purchaseQuantity } },
      {
        gold: purchaseGold,
        marketScore: purchaseMarketScore,
      },
    );
    persist();
    const standingMessage = verifiedScore > 0 && scoreReceipt.exact
      ? `+${money(verifiedScore)} verified standing.`
      : verifiedScore > 0
        ? 'Standing reconciled from the bazaar.'
      : 'Daily verified-standing cap reached; the trade still completed.';
    showToast(progressionMessage(`Online trade complete: ${purchaseQuantity}× ${ITEMS[purchase.item_id].name}. ${standingMessage}`, previousLevel));
    await refreshSocial();
    return;
  }
  if (action === 'open-listing') {
    if (!cloud.signedIn) {
      requestOnlineAccount(element, 'place items in secure escrow');
      return;
    }
    if (social.mine.length >= 30) {
      showToast('Checking your 30 escrow slots…');
      await refreshSocial();
      if (social.mine.length >= 30) {
        showToast('All 30 escrow slots are occupied. Cancel or sell a listing first.');
        return;
      }
    }
    rememberFocus(element);
    listingItemId = element.dataset.item;
    listingDraftPrice = ITEMS[listingItemId]?.value || null;
    listingDraftQuantity = 1;
    modal = 'listing';
    render();
    return;
  }
  if (action === 'listing-price-suggestion') {
    const quantity = Math.max(1, Math.round(Number(document.querySelector('#listing-quantity')?.value) || 1));
    const unitPrice = Math.max(1, Math.round(Number(element.dataset.unitPrice) || 1));
    const input = document.querySelector('#listing-price');
    if (input) input.value = String(Math.min(100000000, quantity * unitPrice));
    updateListingPreview();
    return;
  }
  if (action === 'create-online-listing') {
    const price = Number(document.querySelector('#listing-price')?.value);
    const quantity = Number(document.querySelector('#listing-quantity')?.value);
    const item = ITEMS[listingItemId];
    listingDraftPrice = Number.isFinite(price) ? price : listingDraftPrice;
    listingDraftQuantity = Number.isFinite(quantity) ? quantity : listingDraftQuantity;
    const owned = state.inventory.find(entry => entry.itemId === listingItemId)?.quantity || 0;
    const economics = listingEconomics(quantity, price, owned, item?.value);
    if (!item || !Number.isInteger(quantity) || quantity < 1 || quantity > Math.min(999, owned)) {
      showToast('Choose a bundle quantity you currently own.');
      return;
    }
    if (!economics.valid) {
      showToast(`Choose a total price between ${money(economics.minPrice)} and ${money(economics.maxPrice)} gold.`);
      return;
    }
    const itemId = listingItemId;
    const result = await runMutation(
      'create-listing',
      element,
      'Publishing…',
      () => cloud.createListing(itemId, quantity, price),
      { ledger: true },
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error || 'The keeper could not publish that listing.');
      return;
    }
    const receipt = normalizeMarketListing(
      Array.isArray(result.data) ? result.data[0] : result.data,
      KNOWN_ITEM_IDS,
      { publicBoard: true },
    );
    if (
      !receipt
      || receipt.item_id !== itemId
      || Number(receipt.quantity) !== quantity
      || Number(receipt.price) !== price
    ) {
      showToast('The listing was published, but its escrow receipt was incomplete. Refreshing the ledger.');
      await refreshSocial();
      return;
    }
    const previousLevel = state.level;
    state = awardMarketXp({
      ...state,
      inventory: addInventory(state.inventory, itemId, -quantity),
      journal: [`Online bazaar: listed ${quantity}× ${item.name} for ${money(price)} gold.`, ...state.journal].slice(0, 12),
    }, Math.min(40, 8 + quantity * 2));
    state = ensureCustomerOffer(state);
    rebaseCloudLedger({ inventory: { [itemId]: -quantity } });
    listingItemId = null;
    listingDraftPrice = null;
    listingDraftQuantity = 1;
    modal = null;
    persist();
    showToast(progressionMessage(`${quantity}× ${item.name} is now listed in the shared bazaar.`, previousLevel));
    await refreshSocial();
    restoreFocus();
    return;
  }
  if (action === 'cancel-online-listing') {
    const listingId = element.dataset.listing;
    const expectedListing = social.mine.find(listing => listing.id === listingId);
    if (!expectedListing) {
      showToast('That escrow listing is no longer active. Refreshing the ledger.');
      await refreshSocial();
      return;
    }
    const owned = state.inventory.find(entry => entry.itemId === expectedListing.item_id)?.quantity || 0;
    if (owned + expectedListing.quantity > MAX_INVENTORY_QUANTITY) {
      showToast(`Make room for ${money(expectedListing.quantity)}× ${ITEMS[expectedListing.item_id].name} before cancelling.`);
      return;
    }
    const result = await runMutation(
      `cancel-listing:${listingId}`,
      element,
      'Returning…',
      () => cloud.cancelListing(listingId),
      { ledger: true },
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error || 'The keeper could not cancel that listing.');
      return;
    }
    const receipt = Array.isArray(result.data) ? result.data[0] : result.data;
    const cancellation = normalizeCancellationReceipt(receipt, KNOWN_ITEM_IDS);
    if (
      !cancellation
      || cancellation.item_id !== expectedListing.item_id
      || cancellation.quantity !== expectedListing.quantity
    ) {
      showToast('The listing changed, so the cloud ledger is being reconciled before local stock is updated.');
      await refreshSocial();
      return;
    }
    const quantity = cancellation.quantity;
    const item = ITEMS[cancellation.item_id];
    state = {
      ...state,
      inventory: addInventory(state.inventory, cancellation.item_id, quantity),
      journal: [`Online bazaar: cancelled a listing and returned ${quantity}× ${item.name}.`, ...state.journal].slice(0, 12),
    };
    state = ensureCustomerOffer(state);
    rebaseCloudLedger({ inventory: { [cancellation.item_id]: quantity } });
    persist();
    showToast(`${quantity}× ${item.name} returned from escrow.`);
    await refreshSocial();
    return;
  }
  if (action === 'send-lantern') {
    if (!cloud.signedIn) {
      requestOnlineAccount(element, 'send a guild lantern');
      return;
    }
    const socialId = element.dataset.socialId;
    const result = await runMutation(
      `send-lantern:${socialId}`,
      element,
      'Lighting…',
      () => cloud.sendLantern(socialId),
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error || 'The guild lantern could not be sent.');
      await refreshSocial();
      return;
    }
    const receipt = normalizeLanternReceipt(
      Array.isArray(result.data) ? result.data[0] : result.data,
    );
    if (!receipt) {
      showToast('The lantern was sent, but its receipt was incomplete. Refreshing the guild ledger.');
      await refreshSocial();
      return;
    }
    showToast(`Guild lantern sent to ${receipt.recipient_name}.`);
    await refreshSocial();
    return;
  }
  if (action === 'campaign-contribute') {
    if (!cloud.signedIn) {
      requestOnlineAccount(element, 'dispatch a guild caravan');
      return;
    }
    const result = await runMutation(
      'campaign-contribute',
      element,
      'Dispatching…',
      () => cloud.contributeCampaign(),
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error || 'The caravan could not leave the guild hall.');
      await refreshSocial();
      return;
    }
    const campaign = normalizeCampaignStatus(
      Array.isArray(result.data) ? result.data[0] : result.data,
    );
    if (!campaign) {
      showToast('The caravan departed, but its guild receipt was incomplete.');
      await refreshSocial();
      return;
    }
    social = { ...social, campaign, campaignError: false };
    render();
    showToast('Your caravan added 10 route marks to Lanternwake.');
    return;
  }
  if (action === 'campaign-claim') {
    const tier = Number(element.dataset.tier);
    const reward = CAMPAIGN_TIERS.find(entry => entry.tier === tier);
    if (!reward) {
      showToast('That campaign cache is unavailable.');
      return;
    }
    if (!cloud.signedIn) {
      requestOnlineAccount(element, 'claim this guild cache');
      return;
    }
    const result = await runMutation(
      `campaign-claim:${tier}`,
      element,
      'Claiming…',
      () => cloud.claimCampaignTier(tier),
      { ledger: true },
    );
    if (!result) return;
    if (!result.ok) {
      showToast(result.error || 'The guild cache could not be opened.');
      await refreshSocial();
      return;
    }
    const receipt = normalizeCampaignClaimReceipt(
      Array.isArray(result.data) ? result.data[0] : result.data,
      tier,
    );
    if (!receipt) {
      showToast('The cache opened, but its cloud receipt was incomplete.');
      await refreshSocial();
      return;
    }
    state = {
      ...state,
      gold: receipt.gold,
      gems: receipt.gems,
      marketScore: receipt.market_score,
      journal: [`Lanternwake: claimed the ${reward.title} shared cache.`, ...state.journal].slice(0, 12),
    };
    rebaseCloudLedger({}, {
      gold: state.gold,
      gems: state.gems,
      marketScore: state.marketScore,
    });
    if (social.campaign) {
      social = {
        ...social,
        campaign: {
          ...social.campaign,
          claimed_tiers: [...new Set([...(social.campaign.claimed_tiers || []).map(Number), tier])].sort(),
        },
      };
    }
    persist();
    render();
    showToast(`${reward.title} claimed: ${money(reward.reward.gold)} gold and ${reward.reward.gems} gems.`);
    await refreshSocial();
    return;
  }
  if (action === 'refresh-social') {
    social = { ...social, mode: 'connecting', historyError: false, rosterError: false };
    render();
    await refreshSocial();
  }
  if (action === 'sell-item') commit(sellItem(state, element.dataset.item));
  if (action === 'draw-relic') {
    const result = drawRelics(state, Number(element.dataset.count || 1));
    if (!result.ok) {
      showToast(result.message);
      return;
    }
    state = ensureCustomerOffer(result.state);
    summonResult = result.rewards;
    modal = 'summon-result';
    persist();
    render();
  }
  if (action === 'reset-save') {
    modal = 'reset';
    render();
  }
  if (action === 'confirm-reset') {
    const cleared = clearGameSave(browserStorage, STORAGE_KEY);
    storageHealth = cleared.ok
      ? { issue: null, recoveryRaw: null, error: null }
      : { issue: 'write', recoveryRaw: storageHealth.recoveryRaw, error: cleared.error };
    state = createInitialState();
    onboardingStep = 0;
    modal = 'onboarding';
    collectionFilter = 'All';
    persist();
    showToast('The keeper opened a fresh ledger.');
  }
}

function handleConnectionChange() {
  isOnline = navigator.onLine;
  if (!isOnline) {
    socialRefreshes.invalidate();
    clearTimeout(socialRefreshTimer);
    socialRefreshTimer = null;
  }
  if (!modal) render();
  if (isOnline) refreshSocial();
}

function handleGameStorageChange(event) {
  if (event.key !== STORAGE_KEY) return;
  let value = null;
  let revision = localSaveRevision + 1;
  let writerId = null;
  if (event.newValue !== null) {
    const parsed = parseGameSave(event.newValue);
    if (parsed.status !== 'ok') return;
    value = parsed.value;
    revision = Number.isSafeInteger(Number(value.saveRevision))
      ? Math.max(0, Number(value.saveRevision))
      : 0;
    writerId = typeof value.saveWriterId === 'string' ? value.saveWriterId : null;
    if (writerId === SAVE_WRITER_ID) return;
    if (writerId && revision < localSaveRevision) return;
  }
  if (externalSaveChange && revision < externalSaveChange.revision) return;
  const interruptedModal = externalSaveChange?.interruptedModal ?? modal;
  externalSaveChange = { value, revision, writerId, interruptedModal };
  clearTimeout(cloudSyncTimer);
  clearTimeout(socialRefreshTimer);
  socialRefreshes.invalidate();
  cloudSyncTimer = null;
  socialRefreshTimer = null;
  modal = null;
  focusReturn = null;
  render();
}

async function handleAccountStorageChange(event) {
  const previousUserId = cloud.userId;
  if (!auth.syncStorageEvent(event)) return false;
  accountPending = null;
  clearAccountDraft();
  const nextUserId = cloud.userId;
  socialRefreshes.invalidate();
  clearTimeout(cloudSyncTimer);
  clearTimeout(socialRefreshTimer);
  cloudSyncTimer = null;
  socialRefreshTimer = null;
  cloudReady = false;
  cloudConflict = null;
  if (modal === 'cloud-conflict') modal = null;
  social = {
    mode: cloud.configured ? 'connecting' : 'guest',
    leaders: [],
    listings: [],
    mine: [],
    history: [],
    historyError: false,
    roster: [],
    rosterError: false,
    campaign: null,
    campaignError: false,
  };
  if (nextUserId) {
    await restoreAccount();
    if (previousUserId !== nextUserId) {
      showToast('Cloud account changed in another Melodia tab.');
    }
  } else {
    render();
    await refreshSocial();
    if (previousUserId) showToast('Cloud account signed out in another Melodia tab.');
  }
  return true;
}

function handleBrowserStorageChange(event) {
  handleGameStorageChange(event);
  void handleAccountStorageChange(event);
}

async function registerOfflineShell() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const alreadyControlled = Boolean(navigator.serviceWorker.controller);
  const handleControllerChange = () => {
    if (!alreadyControlled) return;
    pwaUpdateReady = true;
    render();
  };
  navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true });
  try {
    const manifestUrl = document.querySelector('link[rel="manifest"]')?.href || window.location.href;
    const workerUrl = new URL('./service-worker.js', manifestUrl);
    const registration = await navigator.serviceWorker.register(workerUrl, { scope: './' });
    await registration.update().catch(() => {});
  } catch {
    navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    // A blocked service worker never prevents the local game from starting.
  }
}

window.addEventListener('online', handleConnectionChange);
window.addEventListener('offline', handleConnectionChange);
window.addEventListener('storage', handleBrowserStorageChange);
window.addEventListener('load', registerOfflineShell, { once: true });

persist();
render();
restoreAccount();

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') {
    clearTimeout(timer);
    timer = null;
    clearTimeout(socialRefreshTimer);
    socialRefreshTimer = null;
    socialRefreshes.invalidate();
    persist();
    scheduleRuntimeTick();
    return;
  }
  let synchronized = rollDailyState(state);
  synchronized = rollWeeklyState(synchronized);
  synchronized = regenerateEnergy(synchronized);
  if (synchronized !== state) {
    state = synchronized;
    persist();
  }
  render();
  if (isOnline) refreshSocial();
});

async function restoreAccount() {
  if (auth.configured) await auth.restore();
  if (cloud.signedIn) {
    const bootstrap = await cloud.bootstrap();
    cloudReady = reconcileCloudBootstrap(bootstrap) && !cloudConflict;
    if (cloudReady) scheduleCloudSync(0);
  }
  render();
  refreshSocial();
  if (auth.restoreError) {
    showToast(`${auth.restoreError} Your saved account session was preserved for a later retry.`);
  } else if (auth.persistenceError) {
    showToast(auth.signedIn
      ? 'Cloud account restored for this tab, but browser session storage is blocked.'
      : 'An expired account session could not be removed from browser storage.');
  }
}

function scheduleSocialRefresh() {
  clearTimeout(socialRefreshTimer);
  socialRefreshTimer = null;
  if (!shouldPollSocial({
    visible: document.visibilityState === 'visible',
    online: isOnline,
    configured: cloud.configured,
  })) return;
  socialRefreshTimer = setTimeout(() => {
    socialRefreshTimer = null;
    refreshSocial();
  }, SOCIAL_REFRESH_INTERVAL_MS);
}

async function refreshSocial() {
  if (!cloud.configured) return false;
  if (isOnline && auth.configured && (auth.signedIn || auth.restoreError)) {
    await auth.restore();
  }
  scheduleSocialRefresh();
  const ticket = socialRefreshes.begin();
  const requestedUserId = cloud.userId;
  const requestedSignedIn = cloud.signedIn;
  const [leaderboard, market, mine, history, roster, campaign, ledger] = await Promise.all([
    cloud.leaderboard(),
    cloud.market(),
    requestedSignedIn ? cloud.myListings() : Promise.resolve({ ok: true, data: [] }),
    requestedSignedIn ? cloud.tradeHistory() : Promise.resolve({ ok: true, data: [] }),
    requestedSignedIn ? cloud.socialRoster() : Promise.resolve({ ok: true, data: [] }),
    requestedSignedIn ? cloud.campaignStatus() : Promise.resolve({ ok: true, data: null }),
    requestedSignedIn && cloudReady && normalizeCloudSync(state.cloudSync, requestedUserId)
      ? cloud.bootstrap()
      : Promise.resolve({ ok: false }),
  ]);
  if (
    !socialRefreshes.isCurrent(ticket)
    || cloud.userId !== requestedUserId
    || cloud.signedIn !== requestedSignedIn
  ) return false;
  if (ledger.ok) {
    if (reconcileCloudBootstrap(ledger, false)) scheduleCloudSync(0);
    else {
      cloudReady = false;
      cloudSyncError = 'The cloud ledger returned an invalid bootstrap receipt.';
    }
  }
  const campaignData = campaign.ok
    ? normalizeCampaignStatus(Array.isArray(campaign.data) ? campaign.data[0] : campaign.data)
    : null;
  const campaignInvalid = requestedSignedIn && campaign.ok && !campaignData;
  if (!leaderboard.ok && !market.ok) {
    const setupRequired = leaderboard.status === 404 && market.status === 404;
    social = {
      mode: 'guest',
      setupRequired,
      leaders: [],
      listings: [],
      mine: [],
      history: history.ok && Array.isArray(history.data)
        ? history.data.map(entry => normalizeTradeHistoryEntry(entry, KNOWN_ITEM_IDS)).filter(Boolean)
        : social.history,
      historyError: requestedSignedIn && !history.ok,
      roster: roster.ok && Array.isArray(roster.data)
        ? roster.data.map(normalizeRosterEntry).filter(Boolean)
        : [],
      rosterError: cloud.signedIn && !roster.ok,
      campaign: campaignData,
      campaignError: cloud.signedIn && (!campaign.ok || campaignInvalid),
    };
    render();
    return true;
  }
  const ownListings = mine.ok && Array.isArray(mine.data)
    ? mine.data
      .map(listing => normalizeMarketListing(listing, KNOWN_ITEM_IDS))
      .filter(Boolean)
    : [];
  const tradeHistory = history.ok && Array.isArray(history.data)
    ? history.data
      .map(entry => normalizeTradeHistoryEntry(entry, KNOWN_ITEM_IDS))
      .filter(Boolean)
    : social.history;
  social = {
    mode: 'live',
    setupRequired: false,
    leaders: Array.isArray(leaderboard.data)
      ? leaderboard.data.map(normalizeLeaderboardEntry).filter(Boolean)
      : [],
    listings: Array.isArray(market.data)
      ? market.data
        .map(listing => normalizeMarketListing(listing, KNOWN_ITEM_IDS, { publicBoard: true }))
        .filter(Boolean)
      : [],
    mine: ownListings,
    history: tradeHistory,
    historyError: requestedSignedIn && !history.ok,
    roster: roster.ok && Array.isArray(roster.data)
      ? roster.data.map(normalizeRosterEntry).filter(Boolean)
      : [],
    rosterError: cloud.signedIn && !roster.ok,
    campaign: campaignData,
    campaignError: cloud.signedIn && (!campaign.ok || campaignInvalid),
  };
  render();
  return true;
}
